import React, { useState, useEffect, useRef } from 'react';
import { X, Clock, User, Tag, AlertCircle, MessageCircle, Paperclip, Send, Check, XCircle, Pause, Play, Edit3, Calendar, AlertTriangle, Upload, FileText, Download, Eye, Archive, Forward, Reply, Star, Flag, Settings, Link2, Phone, Mail, History, Activity, Save, ArrowLeft, GitBranch, Target, Users, Shield, HelpCircle, ChevronDown, ExternalLink, Building2, Search, DollarSign, FileDown } from 'lucide-react';
import { getTicketDetails, updateTicket, addTicketComment, closeTicket, uploadTicketAttachment, processApproval, getITUsers, getCategories, getSystems, getDepartmentUsers, getHelpdeskDepartments, getHelpdeskRoles, getDivisionDepartments, pauseResumeTicket, getRelatedTickets as apiGetRelatedTickets, createTicketLink as apiCreateTicketLink, deleteTicketLink as apiDeleteTicketLink, getSuppliers, exportTicketPdf } from '../../../../models/helpdeskModel';
import { getSupplier } from '../../../../models/suppliersModel';
import { calculateSLAInfo, enrichTicketWithSLA } from '../../../../utils/slaUtils';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../../../config';
import { useAuth } from '../../../../context/AuthContext';

// Import common components
import PageHeader from '../../../../components/common/PageHeader';
import Button from '../../../../components/common/Button';
import WysiwygEditor from '../../../../components/common/WysiwygEditor';
import MultiFileInput from '../../../../components/common/MultiFileInput';
import Select from '../../../../components/common/Select';

// Import workflow components
import { 
  TicketTypeHeader, 
  ApprovalPanel, 
  DynamicTicketFields, 
  DelegationPanel, 
  SLATracker,
  IntegrationStatusPanel,
  ImplementationPanel
} from './WorkflowComponents';

// Import approval components
import ApproverSelection from './ApproverSelection';
import ApprovalAction from './ApprovalAction';
import AssessmentPanel from './AssessmentPanel';
import ApprovalActionCTA from './ApprovalActionCTA';
import ImplementationOutcome from './ImplementationOutcome';
import DeclarationActionCard from './DeclarationActionCard';

// Import escalation components
import EscalationTab from './EscalationTab';
import SupplierEscalationTab from './SupplierEscalationTab';
import BillingManagementTab from './BillingManagementTab';

const TicketDetailsModal = ({ isOpen, onClose, ticketId, onTicketUpdate, currentUser }) => {
  const { isIT } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [replyContent, setReplyContent] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [userRole, setUserRole] = useState('user'); // user, manager, hod, it_hod, director
  const [showEscalationMenu, setShowEscalationMenu] = useState(false);
  
  // Dynamic data states
  const [itUsers, setItUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([
    { value: 'support', label: 'General Support' },
    { value: 'enhancement', label: 'Enhancement' },
    { value: 'change_request', label: 'Change Request' },
    { value: 'defect', label: 'Defect Report' },
    { value: 'new_user', label: 'New User Setup' },
    { value: 'staff_movement', label: 'Staff Movement' },
    { value: 'access_modification', label: 'Edams Access Modification' },
    { value: 'access_termination', label: 'Access Termination' },
    { value: 'password_reset', label: 'Password Reset' }
  ]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [related, setRelated] = useState([]);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(null);
  const [linkForm, setLinkForm] = useState({ relatedNumber: '', relationType: 'related' });
  const [showRelationHelp, setShowRelationHelp] = useState(false);
  const relationHelpRef = useRef(null);
  const [relationHelpPlacement, setRelationHelpPlacement] = useState('bottom'); // 'top' | 'bottom'
  const relationHelpPopoverRef = useRef(null);
  
  // Reference data for mapping IDs to names
  const [referenceData, setReferenceData] = useState({
    systems: [],
    categories: [],
    departments: [],
    divisionDepartments: [],
    users: [],
    roles: []
  });

  // Conversion state
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (isOpen && ticketId) {
      fetchTicketDetails();
      determineUserRole();
      fetchDynamicData();
      fetchSuppliers();
      fetchRelated();
      loadReferenceData();
    }
  }, [isOpen, ticketId]);
  
  // Load reference data for mapping IDs to names
  const loadReferenceData = async () => {
    try {
      const [systemsRes, categoriesRes, departmentsRes, divisionDepartmentsRes, usersRes, rolesRes] = await Promise.all([
        getSystems().catch(() => ({ data: [] })),
        getCategories().catch(() => ({ data: [] })),
        getHelpdeskDepartments().catch(() => ({ data: [] })),
        getDivisionDepartments().catch(() => ({ data: [] })),
        getDepartmentUsers().catch(() => ({ data: [] })),
        getHelpdeskRoles().catch(() => ({ data: [] }))
      ]);
      
      // **CRITICAL**: Extract data using EXACT same logic as NewTicketModal
      const deptList = departmentsRes?.departments || departmentsRes?.data?.departments || departmentsRes?.data || [];
      const divDeptList = divisionDepartmentsRes?.division_departments || divisionDepartmentsRes?.data?.division_departments || divisionDepartmentsRes?.data || [];
      const roleList = rolesRes?.roles || rolesRes?.data?.roles || rolesRes?.data || [];
      const userList = usersRes?.data?.users || usersRes?.data || [];
      
      setReferenceData({
        systems: systemsRes?.data?.data?.rows || systemsRes?.data || [],
        categories: categoriesRes?.data?.data?.categories || categoriesRes?.data?.categories || categoriesRes?.data || [],
        // **MATCH NewTicketModal EXACTLY**: departments = deptList, divisionDepartments = divDeptList
        departments: deptList,  // From getHelpdeskDepartments() - used by Division field
        divisionDepartments: divDeptList,  // From getDivisionDepartments() - used by Department field
        users: userList,
        roles: roleList
      });
    } catch (error) {
      console.warn('Failed to load reference data for custom fields:', error);
    }
  };

  // Debug logging for IT staff visibility
  useEffect(() => {
    if (ticket) {



      setPriority(ticket.priority || 'medium');
      // Check both status and ticket_status fields, preferring the actual ticket_status
      const currentStatus = ticket.status || ticket.ticket_status || 'open';
      setStatus(currentStatus);
      setAssignedTo(ticket.assigned_to || '');
      setTicketType(ticket.ticket_type || 'support');
    }
  }, [ticket]);

  // Close escalation menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showEscalationMenu && !e.target.closest('.escalation-dropdown')) {
        setShowEscalationMenu(false);
        setSupplierSearchTerm(''); // Clear search when menu closes
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEscalationMenu]);

  // Close the relation help popover when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (relationHelpRef.current && !relationHelpRef.current.contains(e.target)) {
        setShowRelationHelp(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setShowRelationHelp(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  // Compute popover placement (above or below) when opened and on viewport changes
  useEffect(() => {
    if (!showRelationHelp) return;

    const computePlacement = () => {
      try {
        const anchorEl = relationHelpRef.current;
        const popEl = relationHelpPopoverRef.current;
        if (!anchorEl || !popEl) return;
        const anchorRect = anchorEl.getBoundingClientRect();
        const popRect = popEl.getBoundingClientRect();
        const margin = 12;
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;
        // Prefer bottom if it fits, else top, else choose larger side
        if (spaceBelow >= popRect.height + margin) {
          setRelationHelpPlacement('bottom');
        } else if (spaceAbove >= popRect.height + margin) {
          setRelationHelpPlacement('top');
        } else {
          setRelationHelpPlacement(spaceBelow >= spaceAbove ? 'bottom' : 'top');
        }
      } catch (_) {
        // Safe fallback
        setRelationHelpPlacement('bottom');
      }
    };

    // Initial compute after DOM paints
    const id = requestAnimationFrame(computePlacement);
    window.addEventListener('resize', computePlacement);
    window.addEventListener('scroll', computePlacement, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', computePlacement);
      window.removeEventListener('scroll', computePlacement, true);
    };
  }, [showRelationHelp]);

  const determineUserRole = () => {
    // Determine user role based on currentUser data
    // This would typically come from your authentication system
    const role = (currentUser?.role_name || currentUser?.role || 'user').toString().toLowerCase();
    const department = (currentUser?.department || '').toString().toLowerCase();
    const position = (currentUser?.position || '').toString().toLowerCase();
    
    if (position.includes('director') || position.includes('managing director')) {
      setUserRole('director');
    } else if (position.includes('hod') && department.includes('it')) {
      setUserRole('it_hod');
    } else if (position.includes('hod') || position.includes('head of')) {
      setUserRole('hod');
    } else if (position.includes('manager')) {
      setUserRole('manager');
    } else if (department.includes('it') || role.includes('admin')) {
      setUserRole('it_staff');
    } else {
      setUserRole('user');
    }
  };

  const getWorkflowType = (ticket) => {
    const type = ticket?.ticket_type?.toLowerCase() || '';
    const category = ticket?.category?.toLowerCase() || '';
    const subject = ticket?.subject?.toLowerCase() || '';
    
    // Access Management Detection
    if (type.includes('access') || category.includes('access') || 
        subject.includes('new user') || subject.includes('staff movement')) {
      return 'Access Management Workflow';
    }
    
    // Enhancement Request Detection
    if (type.includes('enhancement') || category.includes('enhancement') || 
        subject.includes('feature') || subject.includes('improvement')) {
      return 'Change Request Workflow';
    }
    
    // Defect Detection
    if (type.includes('defect') || type.includes('bug') || category.includes('defect') || 
        category.includes('bug') || subject.includes('error') || subject.includes('issue')) {
      return 'Defect Resolution Process';
    }
    
    // Default to Support Request
    return 'Standard IT Support Process';
  };

  if (!isOpen) return null;

  const fetchTicketDetails = async () => {
    setLoading(true);
    try {
      const response = await getTicketDetails(ticketId);

      if (response.status === 1 && response.data) {
        // Use the enhanced ticket data if available, otherwise transform the raw data
        let ticketData;
        if (response.data.ticket) {
          ticketData = response.data.ticket;

          // Ensure subject field is mapped correctly
          if (!ticketData.subject && ticketData.title) {
            ticketData.subject = ticketData.title;
          }
        } else {
          // Fallback transformation for backward compatibility
          const rawTicket = response.data.row || response.data;



          ticketData = {
            id: rawTicket.ticket_id || 'unknown',
            ticket_number: rawTicket.ticket_number || `#${rawTicket.ticket_id || 'unknown'}`,
            subject: rawTicket.title || rawTicket.ticket_subject || 'No Subject',
            description: rawTicket.ticket_details || rawTicket.description || 'No Description',
            status: rawTicket.ticket_status ? getStatusText(rawTicket.ticket_status) : rawTicket.status || 'open',
            priority: String(rawTicket.ticket_priority || rawTicket.priority || 'medium'),
            ticket_type: rawTicket.ticket_type || rawTicket.type || 'support',
            category: rawTicket.ticket_category || rawTicket.category_name || 'General',
            assigned_to: rawTicket.ticket_assigned_to || rawTicket.assigned_to || rawTicket.assigned_user_id || '',
            assigned_to_name: rawTicket.ticket_assigned_name || rawTicket.assigned_name || 'Unassigned',
            created_at: rawTicket.ticket_time ? new Date(rawTicket.ticket_time * 1000).toISOString() : rawTicket.created_at || new Date().toISOString(),
            created_by: rawTicket.user_full_name || rawTicket.requester_name || null,
            requester_email: rawTicket.requester_email || null
          };

        } 
        
        // Add additional data from response
        ticketData.attachments = response.data.ticket?.attachments || response.data.attachments || [];
        ticketData.messages = response.data.ticket?.conversation || response.data.conversation || response.data.chats || response.data.comments || response.data.messages || [];
        ticketData.conversation = response.data.ticket?.conversation || response.data.conversation || response.data.messages || [];
        ticketData.activity = response.data.ticket?.updates || response.data.updates || response.data.conversation || [];
        ticketData.comments = response.data.ticket?.comments || response.data.comments || [];
        
        // Add custom fields data
        ticketData.custom_fields = response.data.ticket?.custom_fields || response.data.custom_fields || null;
 
        // Parse custom_fields if it's a JSON string
        if (ticketData.custom_fields && typeof ticketData.custom_fields === 'string') {
          try {
            // Try to parse as JSON first
            if (ticketData.custom_fields.startsWith('{') || ticketData.custom_fields.startsWith('[')) {
              ticketData.custom_fields = JSON.parse(ticketData.custom_fields);

            } else if (ticketData.custom_fields.startsWith('a:')) {

            } else {

            }
          } catch (parseError) {

          }
        }
        
        // Additional check: if custom_fields is still a string after conversion attempt,
        // and it looks like it might be malformed, try to handle it
        if (ticketData.custom_fields && typeof ticketData.custom_fields === 'string' && 
            !ticketData.custom_fields.startsWith('{') && !ticketData.custom_fields.startsWith('a:')) {

          // This might be a case where the conversion partially worked but got corrupted
          ticketData.custom_fields = {};
        } 
        // Parse custom_fields if it's a JSON string
        if (ticketData.custom_fields && typeof ticketData.custom_fields === 'string') {
          try {
            // Try to parse as JSON first
            if (ticketData.custom_fields.startsWith('{') || ticketData.custom_fields.startsWith('[')) {
              ticketData.custom_fields = JSON.parse(ticketData.custom_fields);

            } else {

            }
          } catch (parseError) {

          }
        }
    
        // Calculate and add SLA information using utility
        const enrichedTicket = enrichTicketWithSLA(ticketData);
        
        setTicket(enrichedTicket);
      } else {
        toast.error('Failed to fetch ticket details');
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error('Error fetching ticket details');
    } finally {
      setLoading(false);
    }
  };

  // Check if this is an Access Management ticket
  const isAccessManagementTicket = ticket?.ticket_type === 'new_user' || 
                        ticket?.ticket_type === 'staff_movement' || 
                        ticket?.ticket_type === 'access_modification' || 
                        ticket?.ticket_type === 'access_termination' ||
                        ticket?.ticket_type === 'password_reset' ||
                        ticket?.ticket_type === 'access_management';

  // Check if this is a Change Management ticket
  const isChangeManagementTicket = ticket?.ticket_type === 'change_request' ||
                        ticket?.ticket_type === 'enhancement';

  // Combined check for any workflow ticket (for approval panels, etc.)
  const isAccessTicket = isAccessManagementTicket || isChangeManagementTicket;

  // Determine current approval stage
  // Get approvers from ticket data (handle both JSON and comma-separated formats)
  const getApprovers = (approversData) => {
    if (!approversData) return [];
    
    try {
      // Try to parse as JSON first
      return JSON.parse(approversData);
    } catch (e) {
      // If JSON parsing fails, treat as comma-separated string
      return approversData.split(',').map(item => item.trim()).filter(item => item);
    }
  };

  const approvers = getApprovers(ticket?.ticket_approvers);

  // Check if ticket needs approval setup
  const needsApprovalSetup = isAccessTicket && approvers.length === 0;

  // Determine current workflow stage
  const getCurrentWorkflowStage = () => {
    if (!isAccessTicket) return null;
    
    // Debug: Log ticket approval status
    console.log('🔍 Workflow Debug:', {
      ticket_assessment: ticket?.ticket_assessment,
      ticket_manager: ticket?.ticket_manager,
      ticket_hod: ticket?.ticket_hod,
      ticket_it_hod: ticket?.ticket_it_hod,
      ticket_director: ticket?.ticket_director,
      approvers: approvers,
      ticket_level: ticket?.ticket_level,
      ticket_status: ticket?.ticket_status,
      ticket_outcome: ticket?.ticket_outcome
    });
    
    // Check if all approvals are complete
    if (ticket?.ticket_outcome > 0) {
      return { stage: 'completed', message: 'Implementation Complete' };
    }
    
    // Check if ready for implementation (all required approvals complete)
    if (ticket?.ticket_status === 3 || 
        (ticket?.ticket_level === 1 && ticket?.ticket_manager === 1) ||
        (ticket?.ticket_level === 2 && ticket?.ticket_hod === 1) ||
        (ticket?.ticket_level === 3 && ticket?.ticket_it_hod === 1) ||
        (ticket?.ticket_level === 4 && ticket?.ticket_director === 1)) {
      console.log('✅ Ready for implementation check passed');
      return { stage: 'implementation', message: 'Ready for IT Implementation' };
    }
    
    // Determine if this ticket type requires assessment
    const requiresAssessment = ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement';
    const assessmentComplete = requiresAssessment ? ticket?.ticket_assessment === 1 : true;
    
    // Check if approvers are set and assessment is complete (or not required)
    if (assessmentComplete && approvers.length > 0) {
      // Check if all required approvers have approved (value = 1)
      const allApproved = approvers.every(approverType => {
        const result = (() => {
          switch(approverType) {
            case 'manager':
              return ticket?.ticket_manager === 1;
            case 'hod':
              return ticket?.ticket_hod === 1;
            case 'it_hod':
              return ticket?.ticket_it_hod === 1;
            case 'director':
              return ticket?.ticket_director === 1;
            default:
              // Handle specific HOD approvers (e.g., 'hod_93')
              if (approverType.startsWith('hod_')) {
                return ticket?.ticket_hod === 1;
              }
              return false;
          }
        })();
        console.log(`  ${approverType}: ${result} (value: ${ticket?.['ticket_' + approverType]})`);
        return result;
      });
      
      console.log('🎯 All approved check:', allApproved);
      
      if (allApproved) {
        return { stage: 'implementation', message: 'All Approvals Complete - Ready for Implementation' };
      }
      
      // Check current approver needed based on approvers array
      if (approvers.includes('manager') && ticket?.ticket_manager === 0) {
        return { stage: 'manager', message: 'Manager\'s Approval Pending' };
      }
      
      if (approvers.includes('hod') && ticket?.ticket_manager === 1 && ticket?.ticket_hod === 0) {
        return { stage: 'hod', message: 'Department HOD Approval Pending' };
      }
      
      if (approvers.includes('it_hod') && ticket?.ticket_hod === 1 && ticket?.ticket_it_hod === 0) {
        return { stage: 'it_hod', message: 'IT HOD Approval Pending' };
      }
      
      if (approvers.includes('director') && ticket?.ticket_it_hod === 1 && ticket?.ticket_director === 0) {
        return { stage: 'director', message: 'Managing Director Approval Pending' };
      }
      
      return { stage: 'approval_pending', message: 'Approval Process in Progress' };
    }
    
    // Check if assessment is complete (or not required), recommended, but no approvers set yet
    if (assessmentComplete && (ticket?.ticket_recommended === 1 || !requiresAssessment) && approvers.length === 0) {
      return { stage: 'approver_selection', message: 'Select Approvers' };
    }
    
    // Assessment not complete - only for change_request and enhancement tickets
    if (!assessmentComplete) {
      return { stage: 'assessment', message: 'Request Assessment Required' };
    }
    
    return { stage: 'setup', message: 'Workflow Setup Required' };
  };

  // Get workflow status styling based on stage
  const getWorkflowStatusStyling = (stage) => {
    switch (stage) {
      case 'assessment':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          dot: 'bg-yellow-500',
          text: 'text-yellow-800'
        };
      case 'approver_selection':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          dot: 'bg-blue-500',
          text: 'text-blue-800'
        };
      case 'manager':
      case 'hod':
      case 'it_hod':
      case 'director':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          dot: 'bg-orange-500',
          text: 'text-orange-800'
        };
      case 'implementation':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          dot: 'bg-green-500',
          text: 'text-green-800'
        };
      case 'completed':
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          dot: 'bg-gray-500',
          text: 'text-gray-800'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          dot: 'bg-blue-500',
          text: 'text-blue-800'
        };
    }
  };

  const currentWorkflowStage = getCurrentWorkflowStage();

  // Helper function to convert numeric status to text
  const getStatusText = (status) => {
    const statusMap = {
      1: 'open',
      2: 'closed', 
      3: 'escalated',
      10: 'in_progress',
      11: 'paused'
    };
    return statusMap[status] || 'open';
  };

  const handleFieldChange = (fieldName, value) => {
    setTicket(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSetDelegate = async (delegateData) => {
    try {
      // This would call your delegation API
      toast.success('Delegation set successfully');
    } catch (error) {
      console.error('Error setting delegation:', error);
      toast.error('Error setting delegation');
    }
  };

  // Handle assessment submission for access tickets
  const handleAssessmentSubmission = async (assessmentData) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: assessmentData.recommended ? 'Recommend for Approval?' : 'Reject Request?',
      html: assessmentData.recommended 
        ? `<p>You are recommending this request for approval workflow.</p>
           <p style="font-size: 0.9em; color: #6b7280; margin-top: 10px;">
             After submission, you will need to select the required approvers.
           </p>`
        : `<p style="color: #dc2626;">You are marking this request as NOT recommended.</p>
           <p style="font-size: 0.9em; color: #6b7280; margin-top: 10px;">
             This will reject the request and no approval workflow will be initiated.
           </p>`,
      icon: assessmentData.recommended ? 'question' : 'warning',
      showCancelButton: true,
      confirmButtonColor: assessmentData.recommended ? '#10b981' : '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: assessmentData.recommended ? 'Yes, Recommend' : 'Yes, Reject',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true
    });

    // If user didn't confirm, exit
    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await updateTicket(ticketId, {
        ticket_assessment: 1,
        ticket_affected: assessmentData.businessActivities,
        ticket_impacted: assessmentData.possibleImpact,
        ticket_mitigation: assessmentData.mitigationControl,
        ticket_recommended: assessmentData.recommended ? 1 : 2
      });
      
      if (response.status === 1) {
        toast.success('Assessment submitted successfully');
        fetchTicketDetails(); // Refresh ticket details
      } else {
        toast.error(response.message || 'Failed to submit assessment');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Error submitting assessment: ' + error.message);
    }
  };

  // Handle approver selection for access tickets
  const handleApproverSelection = async (approversData) => {
    // Handle both old format (array) and new format (object with approvers and selectedHodIds)
    const selectedApprovers = Array.isArray(approversData) ? approversData : approversData.approvers;
    const selectedHodIds = approversData.selectedHodIds || [];
    const selectionOrder = approversData.selectionOrder || [];
    
    // Validate at least one approver selected
    if (selectedApprovers.length === 0 && selectedHodIds.length === 0) {
      toast.error('Please select at least one approver');
      return;
    }
    
    // Build approval sequence using the literal selection order
    const approvalSequence = [];
    const orderedApproverIds = [];
    
    selectionOrder.forEach(item => {
      if (item.type === 'approver') {
        // Regular approver (manager, it_hod, director)
        orderedApproverIds.push(item.id);
        if (item.id === 'manager') approvalSequence.push('Manager/Supervisor');
        if (item.id === 'it_hod') approvalSequence.push('IT HOD');
        if (item.id === 'director') approvalSequence.push('Managing Director');
      } else if (item.type === 'hod') {
        // HOD approver
        orderedApproverIds.push(`hod_${item.id}`);
        const hod = ticket?.hods?.find(h => h.user_id === item.id);
        if (hod) {
          approvalSequence.push(`HOD (${hod.user_full_name})`);
        }
      }
    });
    
    const approverNames = approvalSequence.join(' → ');

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Confirm Approver Selection',
      html: `
        <p>You are about to set the following approval workflow:</p>
        <div style="margin: 15px 0; padding: 10px; background: #f3f4f6; border-radius: 5px; text-align: left;">
          <strong style="color: #1f2937;">${approverNames}</strong>
        </div>
        <p style="font-size: 0.9em; color: #6b7280;">
          Approvals will be processed <strong>in the exact order shown above</strong>. Notification emails will be sent to each approver.
        </p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Set Approvers',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true
    });

    // If user didn't confirm, exit
    if (!result.isConfirmed) {
      return;
    }

    try {
      // Use the ordered approver IDs array which respects literal selection order
      const updateData = {
        ticket_approvers: JSON.stringify(orderedApproverIds),
        ticket_level: orderedApproverIds.length,
        ticket_recommended: 1  // Mark as recommended to start approval flow
      };
      
      // Store selected HOD IDs in custom_fields for reference
      if (selectedHodIds.length > 0) {
        const customFields = ticket?.custom_fields || {};
        customFields.selected_hod_ids = selectedHodIds;
        updateData.custom_fields = JSON.stringify(customFields);
      }
      
      const response = await updateTicket(ticketId, updateData);
      
      if (response.status === 1) {
        toast.success('Approvers selected successfully');
        fetchTicketDetails(); // Refresh ticket details
      } else {
        toast.error(response.message || 'Failed to set approvers');
      }
    } catch (error) {
      console.error('Error setting approvers:', error);
      toast.error('Error setting approvers: ' + error.message);
    }
  };

  // Handle reset of all approvals
  const handleResetApprovers = async (resetOptions = null) => {
    // If resetOptions is provided (from ApproverSelection component), use it directly
    if (resetOptions) {
      try {
        const updateData = {
          reset_approval_options: resetOptions
        };

        // If resetting approvers, clear the configuration
        if (resetOptions.reset_approvers) {
          updateData.ticket_approvers = JSON.stringify([]);
          updateData.ticket_level = 0;
          updateData.ticket_recommended = 1; // Keep as recommended, just reset approvers
        }

        // Reset individual approval fields if selected
        if (resetOptions.reset_manager) updateData.ticket_manager = 0;
        if (resetOptions.reset_hod) updateData.ticket_hod = 0;
        if (resetOptions.reset_it_hod) updateData.ticket_it_hod = 0;
        if (resetOptions.reset_director) updateData.ticket_director = 0;
        
        const response = await updateTicket(ticketId, updateData);
        
        if (response.status === 1) {
          const resetFields = [];
          if (resetOptions.reset_approvers) resetFields.push('approver configuration');
          if (resetOptions.reset_manager) resetFields.push('manager approval');
          if (resetOptions.reset_hod) resetFields.push('HOD approval');
          if (resetOptions.reset_it_hod) resetFields.push('IT HOD approval');
          if (resetOptions.reset_director) resetFields.push('director approval');
          
          toast.success(`Successfully reset: ${resetFields.join(', ')}`);
          fetchTicketDetails(); // Refresh ticket details
        } else {
          toast.error(response.message || 'Failed to reset approvals');
        }
      } catch (error) {
        console.error('Error resetting approvals:', error);
        toast.error('Error resetting approvals: ' + error.message);
      }
      return;
    }

    // Original SweetAlert modal logic (for direct calls from TicketDetailsModal)
    // Count completed approvals
    const completedApprovals = [];
    if (ticket?.ticket_manager === 1) completedApprovals.push('Manager');
    if (ticket?.ticket_hod === 1) completedApprovals.push('HOD');
    if (ticket?.ticket_it_hod === 1) completedApprovals.push('IT HOD');
    if (ticket?.ticket_director === 1) completedApprovals.push('Director');

    // Build warning message with checkboxes
    let warningHtml = `
      <p style="margin-bottom: 15px;">Select which approval fields to reset:</p>
      <div style="background: #dbeafe; border: 1px solid #93c5fd; border-radius: 6px; padding: 12px; margin-bottom: 15px; text-align: left;">
        <p style="margin: 0; font-size: 0.9em; color: #1e40af;"><strong>Use this when:</strong></p>
        <ul style="margin: 8px 0 0 20px; font-size: 0.9em; color: #1e3a8a;">
          <li>A selected approver is unavailable (on leave, resigned, etc.)</li>
          <li>You need to select a different HOD or approver</li>
          <li>The approval workflow needs to be reconfigured</li>
        </ul>
      </div>
      <div style="margin: 15px 0;">
        <label style="display: flex; align-items: center; margin-bottom: 8px;">
          <input type="checkbox" id="reset_approvers" style="margin-right: 8px;">
          <div>
            <span>Reset Approver Configuration</span>
            <div style="font-size: 0.8em; color: #6b7280; margin-top: 2px;">Clear selected approvers but keep existing approvals</div>
          </div>
        </label>
        <label style="display: flex; align-items: center; margin-bottom: 8px;">
          <input type="checkbox" id="reset_manager" ${ticket?.ticket_manager === 1 ? 'checked' : ''} style="margin-right: 8px;">
          <span>Reset Manager Approval ${ticket?.ticket_manager === 1 ? '(Already Approved)' : ''}</span>
        </label>
        <label style="display: flex; align-items: center; margin-bottom: 8px;">
          <input type="checkbox" id="reset_hod" ${ticket?.ticket_hod === 1 ? 'checked' : ''} style="margin-right: 8px;">
          <span>Reset HOD Approval ${ticket?.ticket_hod === 1 ? '(Already Approved)' : ''}</span>
        </label>
        <label style="display: flex; align-items: center; margin-bottom: 8px;">
          <input type="checkbox" id="reset_it_hod" ${ticket?.ticket_it_hod === 1 ? 'checked' : ''} style="margin-right: 8px;">
          <span>Reset IT HOD Approval ${ticket?.ticket_it_hod === 1 ? '(Already Approved)' : ''}</span>
        </label>
        <label style="display: flex; align-items: center; margin-bottom: 8px;">
          <input type="checkbox" id="reset_director" ${ticket?.ticket_director === 1 ? 'checked' : ''} style="margin-right: 8px;">
          <span>Reset Director Approval ${ticket?.ticket_director === 1 ? '(Already Approved)' : ''}</span>
        </label>
      </div>
    `;

    // Dynamic warning will be added via JavaScript based on checkbox selections

    const result = await Swal.fire({
      title: 'Reset Approval Workflow?',
      html: warningHtml,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Reset Selected',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true,
      didOpen: () => {
        // Add event listeners to checkboxes
        const checkboxes = document.querySelectorAll('input[type="checkbox"]');
        const updateWarningMessage = () => {
          const selectedCompletedApprovals = [];
          if (document.getElementById('reset_manager').checked && ticket?.ticket_manager === 1) {
            selectedCompletedApprovals.push('Manager');
          }
          if (document.getElementById('reset_hod').checked && ticket?.ticket_hod === 1) {
            selectedCompletedApprovals.push('HOD');
          }
          if (document.getElementById('reset_it_hod').checked && ticket?.ticket_it_hod === 1) {
            selectedCompletedApprovals.push('IT HOD');
          }
          if (document.getElementById('reset_director').checked && ticket?.ticket_director === 1) {
            selectedCompletedApprovals.push('Director');
          }

          // Find or create warning div
          let warningDiv = document.querySelector('.dynamic-warning');
          if (selectedCompletedApprovals.length > 0) {
            if (!warningDiv) {
              warningDiv = document.createElement('div');
              warningDiv.className = 'dynamic-warning';
              warningDiv.style.cssText = 'background: #fef3c7; border: 1px solid #fbbf24; border-radius: 6px; padding: 12px; margin: 15px 0; text-align: left;';
              // Insert after the checkboxes div
              const checkboxesDiv = document.querySelector('div[style*="margin: 15px 0"]');
              checkboxesDiv.parentNode.insertBefore(warningDiv, checkboxesDiv.nextSibling);
            }
            warningDiv.innerHTML = `
              <p style="margin: 0; font-size: 0.9em; color: #78350f;">
                <strong>⚠️ Warning:</strong> ${selectedCompletedApprovals.length} completed approval(s) will be cleared: <strong>${selectedCompletedApprovals.join(', ')}</strong>
              </p>
            `;
          } else if (warningDiv) {
            warningDiv.remove();
          }
        };

        checkboxes.forEach(checkbox => {
          checkbox.addEventListener('change', (e) => {
            // Remove the automatic checking/unchecking logic
            // Each checkbox should work independently
            updateWarningMessage();
          });
        });

        // Initial warning message update
        updateWarningMessage();
      }
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Get selected reset options
      const resetApprovers = document.getElementById('reset_approvers').checked;
      const resetManager = document.getElementById('reset_manager').checked;
      const resetHod = document.getElementById('reset_hod').checked;
      const resetItHod = document.getElementById('reset_it_hod').checked;
      const resetDirector = document.getElementById('reset_director').checked;

      const updateData = {};
      
      // Only add reset_approval_options if at least one option is selected
      if (resetApprovers || resetManager || resetHod || resetItHod || resetDirector) {
        updateData.reset_approval_options = {
          reset_approvers: resetApprovers,
          reset_manager: resetManager,
          reset_hod: resetHod,
          reset_it_hod: resetItHod,
          reset_director: resetDirector
        };
      }

      // If resetting approvers, clear the configuration
      if (resetApprovers) {
        updateData.ticket_approvers = JSON.stringify([]);
        updateData.ticket_level = 0;
        updateData.ticket_recommended = 1; // Keep as recommended, just reset approvers
      }

      // Reset individual approval fields if selected
      if (resetManager) updateData.ticket_manager = 0;
      if (resetHod) updateData.ticket_hod = 0;
      if (resetItHod) updateData.ticket_it_hod = 0;
      if (resetDirector) updateData.ticket_director = 0;
      
      const response = await updateTicket(ticketId, updateData);
      
      if (response.status === 1) {
        const resetFields = [];
        if (resetApprovers) resetFields.push('approver configuration');
        if (resetManager) resetFields.push('manager approval');
        if (resetHod) resetFields.push('HOD approval');
        if (resetItHod) resetFields.push('IT HOD approval');
        if (resetDirector) resetFields.push('director approval');
        
        toast.success(`Successfully reset: ${resetFields.join(', ')}`);
        fetchTicketDetails(); // Refresh ticket details
      } else {
        toast.error(response.message || 'Failed to reset approvals');
      }
    } catch (error) {
      console.error('Error resetting approvals:', error);
      toast.error('Error resetting approvals: ' + error.message);
    }
  };

  // Handle approval action (approve/reject/more_info)
  const handleApprovalAction = async (action, approverType, notes = '') => {
    // Determine confirmation message and icon based on action
    let confirmTitle, confirmText, confirmIcon, confirmButtonText, confirmButtonColor;
    
    if (action === 'approved') {
      confirmTitle = 'Approve Request?';
      confirmText = notes 
        ? `You are approving this request with the following comment: "${notes}"`
        : 'You are about to approve this request. This action cannot be undone.';
      confirmIcon = 'success';
      confirmButtonText = 'Yes, Approve';
      confirmButtonColor = '#10b981'; // green
    } else if (action === 'rejected') {
      confirmTitle = 'Reject Request?';
      confirmText = notes 
        ? `You are rejecting this request with the following comment: "${notes}"`
        : 'You are about to reject this request. This will stop the approval workflow.';
      confirmIcon = 'warning';
      confirmButtonText = 'Yes, Reject';
      confirmButtonColor = '#ef4444'; // red
    } else {
      confirmTitle = 'Request More Information?';
      confirmText = notes 
        ? `You will request more information with the following comment: "${notes}"`
        : 'You are requesting more information. The requester will be notified to provide additional details.';
      confirmIcon = 'info';
      confirmButtonText = 'Yes, Request Info';
      confirmButtonColor = '#f59e0b'; // yellow
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: confirmTitle,
      text: confirmText,
      icon: confirmIcon,
      showCancelButton: true,
      confirmButtonColor: confirmButtonColor,
      cancelButtonColor: '#6b7280',
      confirmButtonText: confirmButtonText,
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true
    });

    // If user didn't confirm, exit
    if (!result.isConfirmed) {
      return;
    }

    try {
      const updateData = {};
      // Status values: 1 = Approved, 2 = More Info, 3 = Rejected
      updateData[`ticket_${approverType}`] = action === 'approved' ? 1 : action === 'rejected' ? 3 : 2;
      updateData['approval_action'] = action; // Add action type
      updateData['approval_notes'] = notes; // Add notes
      updateData['approver_type'] = approverType; // Add approver type for backend
      
      const response = await updateTicket(ticketId, updateData);
      
      if (response.status === 1) {
        const actionText = action === 'approved' ? 'approved' : action === 'rejected' ? 'rejected' : 'requested more information';
        toast.success(`${approverType.toUpperCase()} ${actionText} successfully`);
        fetchTicketDetails(); // Refresh ticket details
      } else {
        toast.error(response.message || 'Failed to process approval');
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error('Error processing approval: ' + error.message);
    }
  };

  // Handle resend approval notification
  const handleResendApprovalNotification = async (approverType) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/helpdesk/tickets/${ticketId}/resend-approval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('user_token')}`
        },
        body: JSON.stringify({ approverType })
      });

      const data = await response.json();

      if (data.status === 1) {
        toast.success(`Approval notification resent to ${data.data?.approver || 'approver'}`);
      } else {
        toast.error(data.message || 'Failed to resend notification');
      }
    } catch (error) {
      console.error('Error resending approval notification:', error);
      toast.error('Error resending notification: ' + error.message);
    }
  };

  // Handle asset declaration submission
  const handleDeclarationSubmit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/helpdesk/tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          asset_declared: 'yes'
        })
      });

      const data = await response.json();

      if (data.status === 1) {
        // Refresh ticket details to show updated status
        await fetchTicketDetails();
        toast.success('Asset declaration submitted successfully');
      } else {
        throw new Error(data.message || 'Failed to submit declaration');
      }
    } catch (error) {
      console.error('Error submitting declaration:', error);
      throw error; // Re-throw so DeclarationActionCard can handle it
    }
  };

  // Handle implementation outcome
  const handleImplementationOutcome = async (outcome, deallocateAssets = false, deactivateAccess = false) => {
    // Determine confirmation message based on outcome
    let confirmTitle, confirmText, confirmIcon, confirmButtonColor;
    
    if (outcome === 'successful') {
      confirmTitle = 'Mark as Successfully Implemented?';
      confirmText = deallocateAssets || deactivateAccess
        ? 'This will mark the request as successfully implemented and trigger the selected actions (deallocate assets/deactivate access).'
        : 'This will mark the request as successfully implemented and close the workflow.';
      confirmIcon = 'success';
      confirmButtonColor = '#10b981';
    } else if (outcome === 'extension') {
      confirmTitle = 'Request Extension?';
      confirmText = 'This will request additional time for implementation. The ticket will remain open.';
      confirmIcon = 'info';
      confirmButtonColor = '#3b82f6';
    } else {
      confirmTitle = 'Mark as Failed?';
      confirmText = 'This will mark the implementation as failed. Please ensure you have documented the reason in the comments.';
      confirmIcon = 'warning';
      confirmButtonColor = '#ef4444';
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: confirmTitle,
      text: confirmText,
      icon: confirmIcon,
      showCancelButton: true,
      confirmButtonColor: confirmButtonColor,
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true
    });

    // If user didn't confirm, exit
    if (!result.isConfirmed) {
      return;
    }

    try {
      const updateData = {
        ticket_outcome: outcome === 'successful' ? 1 : outcome === 'extension' ? 2 : 3
      };
      
      if (deallocateAssets) updateData.ticket_deallocate = 1;
      if (deactivateAccess) updateData.ticket_deactivate = 1;
      
      const response = await updateTicket(ticketId, updateData);
      
      if (response.status === 1) {
        toast.success('Implementation outcome recorded successfully');
        fetchTicketDetails(); // Refresh ticket details
      } else {
        toast.error(response.message || 'Failed to record outcome');
      }
    } catch (error) {
      console.error('Error recording outcome:', error);
      toast.error('Error recording outcome: ' + error.message);
    }
  };

  // Convert legacy serialized custom_fields to JSON format
  const convertLegacyCustomFields = async () => {
    if (!ticket || converting) return;
    
    setConverting(true);
    
    try {
      let customFields = ticket.custom_fields;
      
      // Check if custom_fields is a serialized string that needs conversion
      if (typeof customFields === 'string' && customFields.startsWith('a:')) {

        // Send the raw serialized string to the backend with a conversion flag
        const updateData = {
          custom_fields: customFields, // Send raw serialized string
          convert_custom_fields: true, // Flag to tell backend to convert
          ticket_manager: 0,
          ticket_hod: 0,
          ticket_it_hod: 0,
          ticket_director: 0
        };

        const response = await updateTicket(ticketId, updateData);

        if (response.status === 1) {
          toast.success('Legacy custom fields converted successfully and workflow flags reset');
          // Force a complete refresh to ensure we get the updated data
          setTicket(null);
          await fetchTicketDetails(); // Refresh ticket details
        } else {
          console.error('Backend conversion failed:', response);
          toast.error(response.message || 'Failed to convert custom fields');
        }
      } else {
        toast.info('Custom fields are already in the correct format');
      }
    } catch (error) {
      console.error('Error converting custom fields:', error);
      toast.error('Error converting custom fields: ' + error.message);
    } finally {
      setConverting(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const updateData = {
        status,
        priority,
        ticket_type: ticketType,
        assigned_to: assignedTo
      };

      const response = await updateTicket(ticketId, updateData);
      if (response.status === 1) {
        // Use updated ticket from API response if available (includes assigned_name, etc.)
        let updatedTicket;
        if (response.ticket) {
          // API returned complete updated ticket - use it
          updatedTicket = { ...ticket, ...response.ticket };
        } else {
          // Fallback: manually merge update data
          updatedTicket = { ...ticket, ...updateData };
        }
        
        const enrichedTicket = enrichTicketWithSLA(updatedTicket);
        
        setTicket(enrichedTicket);
        onTicketUpdate && onTicketUpdate();
        setIsEditing(false);
        toast.success('Ticket updated successfully');
      } else {
        toast.error(response.message || 'Failed to update ticket');
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Error updating ticket: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await getSuppliers();
      
      // Handle the response structure
      let suppliersData = [];
      if (response.status === 'success' && response.data) {
        suppliersData = response.data;
      } else if (response.data) {
        suppliersData = Array.isArray(response.data) ? response.data : [];
      }
      
      // Filter suppliers that have contact information and a valid name
      const suppliersWithContacts = suppliersData.filter(supplier => {
        // Check if supplier has a valid name (BP_Name)
        const hasName = supplier.BP_Name && supplier.BP_Name.trim() !== '' && supplier.BP_Name !== 'null';
        
        // Check for contact information - either E_Mail or supplier users with emails
        const hasPrimaryEmail = supplier.E_Mail && supplier.E_Mail.trim() !== '' && supplier.E_Mail.includes('@');
        const hasUserEmails = supplier.users && supplier.users.some(user => 
          user.supplier_user_email && user.supplier_user_email.includes('@')
        );
        const hasContactInfo = hasPrimaryEmail || hasUserEmails || 
                              (supplier.Mobile_Phone && supplier.Mobile_Phone.trim() !== '') ||
                              (supplier.Contact_Person && supplier.Contact_Person.trim() !== '');
        
        return hasName && hasContactInfo;
      });
      

      
      // Transform the suppliers to have consistent field names for the UI
      const transformedSuppliers = suppliersWithContacts.map(supplier => {
        // Get primary email from E_Mail field
        const primaryEmail = supplier.E_Mail || supplier.Email || supplier.Contact_Email || '';
        
        // Get CC emails from supplier users
        const ccEmails = [];
        if (supplier.users && Array.isArray(supplier.users)) {
          supplier.users.forEach(user => {
            if (user.supplier_user_email && user.supplier_user_email.includes('@')) {
              ccEmails.push(user.supplier_user_email);
            }
          });
        }
        
        return {
          supplier_id: supplier.BP_Code,
          supplier_name: supplier.BP_Name,
          supplier_email: primaryEmail,
          supplier_cc_emails: ccEmails,
          contact_person: supplier.Contact_Person || '',
          mobile_phone: supplier.Mobile_Phone || '',
          tel: supplier.Tel || '',
          group_code: supplier.Group_Code || '',
          users: supplier.users || []
        };
      });
      
      setSuppliers(transformedSuppliers);
      
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchDynamicData = async () => {
    try {
      // Fetch IT users for assignment
      const itUsersResponse = await getITUsers();
      if (itUsersResponse.status === 1 && itUsersResponse.data && itUsersResponse.data.agents) {
        setItUsers(itUsersResponse.data.agents);
      }

      // Set ticket types
      const types = [
        { value: 'support', label: 'General Support' },
        { value: 'enhancement', label: 'Enhancement' },
        { value: 'change_request', label: 'Change Request'},
        { value: 'defect', label: 'Defect Report' },
        { value: 'new_user', label: 'New User Setup' },
        { value: 'staff_movement', label: 'Staff Movement' },
        { value: 'access_modification', label: 'Edams Access Modification' },
        { value: 'access_termination', label: 'Access Termination' },
        { value: 'password_reset', label: 'Password Reset' }
      ];
      setTicketTypes(types);

      // Set default statuses (should come from API)
      setStatuses([
        { value: 'open', label: 'Open' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'pending', label: 'Pending' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'completed', label: 'Completed' },
        { value: 'closed', label: 'Closed' },
        { value: 'escalated', label: 'Escalated' }
      ]);

      // Set default priorities (should come from API)
      setPriorities([
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' }
      ]);

    } catch (error) {
      console.error('Error fetching dynamic data:', error);
      // Set fallback data
      setItUsers([]);
      setTicketTypes([]);
      setStatuses([]);
      setPriorities([]);
    }
  };

  const handleSendReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setSaving(true);
    try {
      const messageData = {
        content: replyContent.trim(),
        type: 'reply',
        is_public: true
      };

      const response = await addTicketComment(ticketId, messageData);
      
      if (response.status === 1) {
        setReplyContent('');
        setSelectedFiles([]);
        fetchTicketDetails();
        toast.success('Reply sent successfully');
      } else {
        toast.error(response.message || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Error sending reply: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendAndClose = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setSaving(true);
    try {
      const messageData = {
        content: replyContent.trim(),
        type: 'reply',
        is_public: true
      };

      const replyResponse = await addTicketComment(ticketId, messageData);
      if (replyResponse.status !== 1) {
        toast.error(replyResponse.message || 'Failed to send reply');
        return;
      }

      const closeResponse = await closeTicket(ticketId);
      if (closeResponse.status !== 1) {
        toast.error(closeResponse.message || 'Reply sent, but failed to close ticket');
        fetchTicketDetails();
        onTicketUpdate && onTicketUpdate();
        return;
      }

      setReplyContent('');
      setSelectedFiles([]);
      fetchTicketDetails();
      onTicketUpdate && onTicketUpdate();
      toast.success('Reply sent and ticket closed successfully');
    } catch (error) {
      console.error('Error sending & closing:', error);
      toast.error('Error sending & closing: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddInternalNote = async () => {
    if (!internalNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setSaving(true);
    try {
      const noteData = {
        content: internalNote.trim(),
        type: 'internal_note',
        is_public: false
      };

      const response = await addTicketComment(ticketId, noteData);
      
      if (response.status === 1) {
        setInternalNote('');
        fetchTicketDetails();
        toast.success('Internal note added successfully');
      } else {
        toast.error(response.message || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Error adding note: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      const response = await updateTicket(ticketId, { status: newStatus });
      if (response.status === 1) {
        // Use updated ticket from API response if available (includes assigned_name, etc.)
        let updatedTicket;
        if (response.ticket) {
          // API returned complete updated ticket - use it
          updatedTicket = { ...ticket, ...response.ticket };
        } else {
          // Fallback: manually update status
          updatedTicket = { ...ticket, status: newStatus };
        }
        
        const enrichedTicket = enrichTicketWithSLA(updatedTicket);
        
        setTicket(enrichedTicket);
        setStatus(enrichedTicket.status);
        onTicketUpdate && onTicketUpdate();
        toast.success('Status updated successfully');
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    }
  };

  const handlePauseResume = async (action) => {
    try {

      const response = await pauseResumeTicket(ticketId, action);

      if (response.status === 1) {
        // Update ticket with new status and timing info
        const updatedTicket = { 
          ...ticket, 
          status: response.data?.status || (action === 'start' ? 'in_progress' : action === 'pause' ? 'paused' : 'in_progress'),
          timing: response.data?.timing 
        };
        const enrichedTicket = enrichTicketWithSLA(updatedTicket);
        
        setTicket(enrichedTicket);
        setStatus(updatedTicket.status);
        onTicketUpdate && onTicketUpdate();
        
        const actionMessages = {
          start: 'Work started on ticket',
          pause: 'Ticket paused',
          resume: 'Work resumed on ticket'
        };
        toast.success(actionMessages[action] || 'Status updated successfully');
      } else {
        console.error('API returned error:', response);
        toast.error(response.message || `Failed to ${action} ticket`);
      }
    } catch (error) {
      console.error(`Error ${action}ing ticket:`, error);
      toast.error(`Error ${action}ing ticket`);
    }
  };

  // Related tickets
  const fetchRelated = async () => {
    try {
      const res = await apiGetRelatedTickets(ticketId);
      if (res.status === 1) setRelated(res.data.related || []);
    } catch (e) {
      console.error('Failed to fetch related tickets', e);
    }
  };

  const handleCreateLink = async () => {
    if (!linkForm.relatedNumber) return;
    setLinking(true);
    try {
      const res = await apiCreateTicketLink(ticketId, linkForm.relatedNumber.trim(), linkForm.relationType);
      if (res.status === 1) {
        await fetchRelated();
        setLinkForm({ relatedNumber: '', relationType: 'related' });
        toast.success(res.data?.message || 'Link created');
      } else {
        toast.error(res.message || 'Failed to create link');
      }
    } catch (e) {
      toast.error('Error creating link');
    } finally {
      setLinking(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Remove Ticket Link?',
      text: 'This will remove the relationship between these tickets. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Remove Link',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true
    });

    // If user didn't confirm, exit
    if (!result.isConfirmed) {
      return;
    }

    setUnlinking(linkId);
    try {
      const res = await apiDeleteTicketLink(linkId);
      if (res.status === 1) {
        await fetchRelated();
        toast.success('Link removed');
      } else {
        toast.error(res.message || 'Failed to remove link');
      }
    } catch (e) {
      toast.error('Error removing link');
    } finally {
      setUnlinking(null);
    }
  };

  // Filter suppliers based on search term
  const getFilteredSuppliers = () => {
    if (!supplierSearchTerm.trim()) {
      return suppliers;
    }
    
    const searchLower = supplierSearchTerm.toLowerCase().trim();
    return suppliers.filter(supplier => 
      supplier.supplier_name?.toLowerCase().includes(searchLower) ||
      supplier.contact_email?.toLowerCase().includes(searchLower) ||
      supplier.contact_person?.toLowerCase().includes(searchLower) ||
      supplier.mobile_phone?.includes(searchLower)
    );
  };

  const handleSupplierSelect = async (supplier) => {
    // Try to fetch detailed supplier information that might include email
    try {
      const detailedResponse = await getSupplier(supplier.supplier_id);
      
      if (detailedResponse && detailedResponse.status === 'success' && detailedResponse.data) {
        // Merge the detailed data with the basic supplier info
        const enhancedSupplier = {
          ...supplier,
          ...detailedResponse.data
        };
        
        setSelectedSupplier(enhancedSupplier);
      } else {
        // Fall back to basic supplier info if detailed fetch fails
        setSelectedSupplier(supplier);
      }
    } catch (error) {
      setSelectedSupplier(supplier);
    }
    
    setActiveTab('supplier_escalation');
    setShowEscalationMenu(false);
    setSupplierSearchTerm(''); // Clear search when supplier is selected
  };

  const handleTabChange = (tabName) => {
    // Clear selected supplier when switching away from supplier escalation tab
    if (activeTab === 'supplier_escalation' && tabName !== 'supplier_escalation') {
      setSelectedSupplier(null);
    }
    setActiveTab(tabName);
  };

  const handleCloseModal = () => {
    setTicket(null);
    setActiveTab('details');
    setReplyContent('');
    setInternalNote('');
    setSelectedFiles([]);
    setSelectedSupplier(null);
    setIsEditing(false);
    onClose();
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'var(--color-danger)';
      case 'medium': return 'var(--color-warning)';
      case 'low': return 'var(--color-success)';
      default: return 'var(--color-info)';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'var(--color-info)';
      case 'in_progress': return 'var(--color-warning)';
      case 'paused': return '#f59e0b'; // Orange for paused
      case 'resolved': return 'var(--color-success)';
      case 'completed': return 'var(--color-success)';
      case 'closed': return 'var(--color-success)';
      case 'escalated': return 'var(--color-danger)';
      default: return 'var(--color-muted)';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        
        {/* Professional Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <div className="grid grid-cols-3 gap-3 relative">
            
            {/* Left Column - Ticket Info (1/3 width) */}
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 leading-tight">
                  {ticket?.ticket_number || `Ticket #${ticketId}`}
                </h1>
                <p className="text-sm text-gray-600 leading-tight mt-1">
                  {ticket?.subject || 'Loading ticket details...'}
                </p>
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="absolute left-1/3 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>

            {/* Right Column - Actions and Requester (2/3 width) */}
            <div className="col-span-2 space-y-3">
              
              {/* Top Row - Status, Priority, Actions and Close */}
              <div className="flex items-center justify-between">
                
                {/* Status & Priority Badges */}
                {ticket && (
                  <div className="flex items-center space-x-2">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: getStatusColor(ticket.status) }}
                    >
                      {ticket.status?.replace('_', ' ').toUpperCase()}
                    </span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{ 
                        color: getPriorityColor(ticket.priority),
                        backgroundColor: `${getPriorityColor(ticket.priority)}20`
                      }}
                    >
                      {ticket.priority?.toUpperCase()} PRIORITY
                    </span> 
                   
                  </div>
                )}

                {/* Action Buttons - More Prominent (only for IT users) */}
                <div className="flex items-center space-x-1">
                  {ticket && isIT() && (
                    <>
                      <button
                        onClick={() => {
                          // If we're not on the details tab, switch to it first
                          if (activeTab !== 'details') {
                            setActiveTab('details');
                          }
                          // Toggle edit mode
                          setIsEditing(!isEditing);
                        }}
                        className={`px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm ${
                          isEditing 
                            ? 'bg-orange-500 text-white hover:bg-orange-600' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        {isEditing ? <Eye className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        <span>{isEditing ? 'View' : 'Edit'}</span>
                      </button>
                      
                      {/* Export PDF Button - Access Management tickets */}
                      {isAccessManagementTicket && (
                        <button
                          onClick={() => exportTicketPdf(ticketId)}
                          className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-purple-500 text-white hover:bg-purple-600"
                          title="Export Access Management Report"
                        >
                          <FileDown className="w-4 h-4" />
                          <span>Export</span>
                        </button>
                      )}
                      
                      {/* Export PDF Button - Change Management tickets */}
                      {isChangeManagementTicket && (
                        <button
                          onClick={() => exportTicketPdf(ticketId)}
                          className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-teal-500 text-white hover:bg-teal-600"
                          title="Export Change Management Report"
                        >
                          <FileDown className="w-4 h-4" />
                          <span>Export</span>
                        </button>
                      )}
                      
                      {ticket.status !== 'closed' && (
                        <>
                          {/* Pause button for open tickets - eliminates the "Start" concept */}
                          {ticket.status === 'open' && (
                            <button
                              onClick={() => handlePauseResume('start')} // This will start work and immediately make it pausable
                              className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-orange-500 text-white hover:bg-orange-600"
                            >
                              <Pause className="w-4 h-4" />
                              <span>Pause</span>
                            </button>
                          )}
                          
                          {/* Pause button for in-progress tickets */}
                          {ticket.status === 'in_progress' && (
                            <>
                              <button
                                onClick={() => handlePauseResume('pause')}
                                className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-orange-500 text-white hover:bg-orange-600"
                              >
                                <Pause className="w-4 h-4" />
                                <span>Pause</span>
                              </button>
                              <button
                                onClick={() => handleStatusUpdate('closed')}
                                className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-green-500 text-white hover:bg-green-600"
                              >
                                <Check className="w-4 h-4" />
                                <span>Resolve</span>
                              </button>
                            </>
                          )}
                          
                          {/* Resume button for paused tickets */}
                          {ticket.status === 'paused' && (
                            <>
                              <button
                                onClick={() => handlePauseResume('resume')}
                                className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-blue-500 text-white hover:bg-blue-600"
                              >
                                <Play className="w-4 h-4" />
                                <span>Resume</span>
                              </button>
                              <button
                                onClick={() => handleStatusUpdate('closed')}
                                className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-green-500 text-white hover:bg-green-600"
                              >
                                <Check className="w-4 h-4" />
                                <span>Resolve</span>
                              </button>
                            </>
                          )}
                          
                          {/* Escalate button - always available for non-closed tickets */}
                          <div className="relative escalation-dropdown">
                            <button
                              onClick={() => {
                                setShowEscalationMenu(!showEscalationMenu);
                                if (!showEscalationMenu) {
                                  setSupplierSearchTerm(''); // Clear search when opening menu
                                }
                              }}
                              className="px-3 py-1.5 rounded-md text-sm h-8 flex items-center space-x-1.5 transition-colors font-medium shadow-sm bg-red-500 text-white hover:bg-red-600"
                            >
                              <AlertTriangle className="w-4 h-4" />
                              <span>Escalate</span>
                              <ChevronDown className="w-3 h-3" />
                            </button>
                            
                            {showEscalationMenu && (
                              <div className="absolute right-0 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
                                <div className="py-1">
                                  <button
                                    onClick={() => {
                                      handleTabChange('escalation');
                                      setShowEscalationMenu(false);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    External Support
                                  </button>
                                  
                                  {/* Supplier Collaborators Section */}
                                  {suppliers.length > 0 && (() => {
                                    const filteredSuppliers = getFilteredSuppliers();
                                    return (
                                      <>
                                        <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-t border-gray-100 mt-1 pt-2">
                                          Supplier Collaborators ({filteredSuppliers.length})
                                        </div>
                                        
                                        {/* Search Input */}
                                        <div className="px-4 py-2 border-b border-gray-100">
                                          <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                              type="text"
                                              placeholder="Search suppliers..."
                                              value={supplierSearchTerm}
                                              onChange={(e) => setSupplierSearchTerm(e.target.value)}
                                              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                                            />
                                          </div>
                                        </div>
                                        
                                        {/* Suppliers List */}
                                        <div className="max-h-48 overflow-y-auto">
                                          {filteredSuppliers.length > 0 ? (
                                            filteredSuppliers.map((supplier) => (
                                              <button
                                                key={supplier.supplier_id}
                                                onClick={() => handleSupplierSelect(supplier)}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                                title={`${supplier.supplier_name} - ${supplier.contact_email || supplier.contact_person || supplier.mobile_phone}`}
                                              >
                                                <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                                                <div className="flex-1 min-w-0">
                                                  <div className="truncate font-medium">{supplier.supplier_name}</div>
                                                  <div className="truncate text-xs text-gray-500">
                                                    {supplier.contact_email || 
                                                     (supplier.contact_person && supplier.mobile_phone ? `${supplier.contact_person} - ${supplier.mobile_phone}` : supplier.contact_person || supplier.mobile_phone)}
                                                  </div>
                                                </div>
                                              </button>
                                            ))
                                          ) : (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                              No suppliers found matching "{supplierSearchTerm}"
                                            </div>
                                          )}
                                        </div>
                                      </>
                                    );
                                  })()}
                                  
                                  {suppliers.length === 0 && (
                                    <button
                                      onClick={() => {
                                        handleTabChange('supplier_escalation');
                                        setShowEscalationMenu(false);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                    >
                                      <Building2 className="w-4 h-4 mr-2" />
                                      Add Supplier Collaborator
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Close Button - Visible but Secondary */}
                  <button
                    onClick={handleCloseModal}
                    className="px-2 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors flex items-center space-x-1 text-sm ml-3 border border-gray-300"
                    aria-label="Close modal"
                    title="Close Ticket"
                  >
                    <X className="w-4 h-4" />
                    <span>Close</span>
                  </button>
                </div>
              </div>

              {/* Bottom Row - Requester Info with Workflow Status */}
              {ticket && (
                <div className="flex items-center justify-between text-sm text-gray-600">
                  {/* Left side - Requester Info */}
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2 text-gray-400" />
                    {ticket.created_by && (
                      <>
                        <span className="font-medium text-gray-900">{ticket.created_by}</span>
                        <span className="mx-2 text-gray-400">|</span>
                      </>
                    )}
                    {ticket.requester_email && (
                      <>
                        <span>{ticket.requester_email}</span>
                        {ticket.requester_department && <span className="mx-2 text-gray-400">|</span>}
                      </>
                    )}
                    {ticket.requester_department && (
                      <span>{ticket.requester_department}</span>
                    )}
                  </div>

                  {/* Right side - Workflow Status */}
                  {isAccessTicket && currentWorkflowStage && (() => {
                    const styling = getWorkflowStatusStyling(currentWorkflowStage.stage);
                    return (
                      <div className={`flex items-center space-x-2 ${styling.bg} ${styling.border} border rounded-lg px-3 py-1.5 text-sm`}>
                        <div className={`w-2 h-2 ${styling.dot} rounded-full ${currentWorkflowStage.stage !== 'completed' ? 'animate-pulse' : ''}`}></div>
                        <span className={`${styling.text} font-medium`}>
                          {currentWorkflowStage.message}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading ticket details...</p>
            </div>
          </div>
        ) : ticket ? (
          <div className="flex-1 flex overflow-hidden">
            
            {/* Main Content Area - Full Width */}
            <div className="flex-1 flex flex-col overflow-hidden">
              
              {/* Tab Navigation with SLA Tracking */}
              <div className="bg-white border-b border-gray-200">
                <div className="grid grid-cols-3 gap-4 px-6 py-4">
                  
                  {/* Left Column - Tabs */}
                  <div className="col-span-2">
                    <nav className="flex space-x-8">
                      <button
                        onClick={() => handleTabChange('details')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'details'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Ticket Details
                      </button>
                      
                      {/* Show approval tab for approvers or IT staff */}
                      {((userRole === 'manager' || userRole === 'hod' || userRole === 'it_hod' || userRole === 'director') && ticket?.requires_approval) || isIT() ? (
                        <button
                          onClick={() => handleTabChange('workflow')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'workflow'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Workflow
                        </button>
                      ) : null}
                      
                      <button
                        onClick={() => handleTabChange('conversation')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'conversation'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Conversation ({ticket.messages?.length || 0})
                      </button>
                      <button
                        onClick={() => handleTabChange('reply')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'reply'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Reply
                      </button>
                      <button
                        onClick={() => handleTabChange('internal')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'internal'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Internal Note
                      </button>
                      <button
                        onClick={() => handleTabChange('escalation')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'escalation'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        External Support
                      </button>
                      
                      {/* Show delegation tab for managers and above */}
                      {(userRole === 'manager' || userRole === 'hod' || userRole === 'it_hod' || userRole === 'director') && (
                        <button
                          onClick={() => handleTabChange('delegation')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'delegation'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Users className="w-4 h-4 inline mr-1" />
                          Delegation
                        </button>
                      )}

                      {/* Show billing tab for IT staff when ticket has supplier */}
                      {isIT() && ticket?.supplier_id && (
                        <button
                          onClick={() => handleTabChange('billing')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'billing'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <DollarSign className="w-4 h-4 inline mr-1" />
                          Billing
                        </button>
                      )}
                    </nav>
                  </div>

                  {/* Right Column - SLA Tracking */}
                  <div className="flex justify-end space-x-8">
                    <div className="text-right">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Response Time</div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-bold text-gray-900">
                          {ticket?.response_time || '--'}
                        </span>
                        {ticket?.response_time && (
                          <>
                            <span className="text-gray-400">|</span>
                            <div className="flex items-center space-x-1">
                              {ticket?.response_status === 'on_track' ? (
                                <Check className="w-3 h-3 text-green-600" />
                              ) : ticket?.response_status === 'overdue' ? (
                                <XCircle className="w-3 h-3 text-red-600" />
                              ) : (
                                <AlertTriangle className="w-3 h-3 text-orange-600" />
                              )}
                              <span className={`text-xs ${
                                ticket?.response_status === 'on_track' ? 'text-green-600' :
                                ticket?.response_status === 'overdue' ? 'text-red-600' :
                                'text-orange-600'
                              }`}>
                                {ticket?.response_status === 'on_track' ? 'On Track' :
                                 ticket?.response_status === 'overdue' ? 'Overdue' :
                                 ticket?.response_status === 'due_soon' ? 'Due Soon' : 'Pending'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Resolution Time</div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-bold text-gray-900">
                          {ticket?.resolution_time || '--'}
                        </span>
                        {ticket?.resolution_time && (
                          <>
                            <span className="text-gray-400">|</span>
                            <div className="flex items-center space-x-1">
                              {ticket?.resolution_status === 'on_track' ? (
                                <Check className="w-3 h-3 text-green-600" />
                              ) : ticket?.resolution_status === 'overdue' ? (
                                <XCircle className="w-3 h-3 text-red-600" />
                              ) : (
                                <AlertTriangle className="w-3 h-3 text-orange-600" />
                              )}
                              <span className={`text-xs ${
                                ticket?.resolution_status === 'on_track' ? 'text-green-600' :
                                ticket?.resolution_status === 'overdue' ? 'text-red-600' :
                                'text-orange-600'
                              }`}>
                                {ticket?.resolution_status === 'on_track' ? 'On Track' :
                                 ticket?.resolution_status === 'overdue' ? 'Overdue' :
                                 ticket?.resolution_status === 'due_soon' ? 'Due Soon' : 'Pending'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
                {activeTab === 'details' && (
                  <div className="max-w-full mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Left Column - Main Ticket Content */}
                      <div className="lg:col-span-2 space-y-6">
                        
                        {/* Dynamic Ticket Fields */}
                        <DynamicTicketFields 
                          ticket={ticket} 
                          isEditing={isEditing} 
                          onFieldChange={handleFieldChange} 
                        />
                        
                        {/* Ticket Description */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                              <MessageCircle className="w-5 h-5 mr-2 text-blue-600" />
                              Ticket Description
                            </h2>
                            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              {getWorkflowType(ticket)}
                            </span>
                          </div>
                          <div className="prose max-w-none">
                            {ticket.description && ticket.description.trim() ? (
                              <div className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                                {ticket.description.includes('<') ? (
                                  <div 
                                    dangerouslySetInnerHTML={{ 
                                      __html: ticket.description
                                        // Remove only dangerous tags, preserve formatting and images
                                        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                                        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                                        .replace(/<link[^>]*>/gi, '')
                                        .replace(/<form[^>]*>[\s\S]*?<\/form>/gi, '')
                                        // Ensure images are properly displayed
                                        .replace(/<img([^>]*)>/gi, '<img$1 class="img-fluid rounded max-w-full h-auto" />')
                                        // Convert line breaks
                                        .replace(/\n/g, '<br>')
                                    }}
                                  />
                                ) : (
                                  <div className="whitespace-pre-line">{ticket.description}</div>
                                )}
                              </div>
                            ) : (
                              <div className="text-gray-500 italic bg-gray-50 p-4 rounded-lg">
                                No description provided.
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Compact IT Assessment Summary - Show between description and additional info for approvers */}
                        {ticket?.ticket_assessment === 1 && (ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement') && (
                          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                            <h2 className="text-lg font-semibold text-blue-900 flex items-center mb-3">
                              <FileText className="w-5 h-5 mr-2 text-blue-600" />
                              IT Assessment Summary
                              <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${ticket?.ticket_recommended === 1 ? 'bg-green-100 text-green-700' : ticket?.ticket_recommended === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                {ticket?.ticket_recommended === 1 ? '✓ Recommended' : ticket?.ticket_recommended === 0 ? '✗ Not Recommended' : 'Pending'}
                              </span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              <div>
                                <span className="font-medium text-blue-800">Business Impact:</span>
                                <p className="text-gray-700 mt-1 line-clamp-2">{ticket?.ticket_affected || 'Not specified'}</p>
                              </div>
                              <div>
                                <span className="font-medium text-blue-800">Possible Impact:</span>
                                <p className="text-gray-700 mt-1 line-clamp-2">{ticket?.ticket_impacted || 'Not specified'}</p>
                              </div>
                              <div>
                                <span className="font-medium text-blue-800">Mitigation:</span>
                                <p className="text-gray-700 mt-1 line-clamp-2">{ticket?.ticket_mitigation || 'Not specified'}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Custom Fields */}
                        {ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0 && (
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-4">
                              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Settings className="w-5 h-5 mr-2 text-blue-600" />
                                Additional Information
                              </h2>
                              
                              {/* Legacy Data Conversion Button - Only visible to IT */}
                              {isIT && ticket.custom_fields && typeof ticket.custom_fields === 'string' && ticket.custom_fields.startsWith('a:') && (
                                <div className="flex items-center space-x-2 bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200">
                                  <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                                  <span className="text-sm text-yellow-700 font-medium whitespace-nowrap">
                                    Legacy Data Format Detected
                                  </span>
                                  <button
                                    onClick={convertLegacyCustomFields}
                                    disabled={converting}
                                    className="ml-2 px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 border border-yellow-400 rounded hover:bg-yellow-300 hover:border-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                  >
                                    {converting ? 'Converting...' : 'Convert'}
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {Object.entries(ticket.custom_fields).map(([key, value]) => {
                                // Define all standard ticket fields that should NOT be shown in Additional Information
                                const standardFields = [
                                  'title', 'subject', 'description', 'priority', 'category', 'system_id', 'type', 
                                  'requester_id', 'requester_email', 'requester_name', 'requester_type',
                                  'status', 'assigned_to', 'created_at', 'updated_at', 'ticket_number',
                                  'source', 'source_reference', 'ticket_email_id', 'supplier_id', 'supplier_user_id',
                                  'attachments', 'custom_fields', 'integration_results',
                                  'declaration' // User's confirmation that form info is correct - not relevant to ticket details
                                ];
                                
                                // Only show fields that are NOT standard ticket fields
                                if (standardFields.includes(key)) {
                                  return null;
                                }
                                
                                // Skip empty or null values
                                if (value === null || value === undefined || value === '' || 
                                    (Array.isArray(value) && value.length === 0) ||
                                    (typeof value === 'object' && Object.keys(value).length === 0)) {
                                  return null;
                                }
                                
                                // Format the key to be more readable
                                const formatKey = (key) => {
                                  // **CRITICAL**: If we used fallback mapping, we need to swap the labels
                                  // This flag will be set by the mapping function when fallback is used
                                  if (key === 'department' && window.usedFallbackForDepartment) {
                                    return 'Division'; // Data was found in divisions array, so it's actually a division
                                  }
                                  if (key === 'division' && window.usedFallbackForDivision) {
                                    return 'Department'; // Data was found in parent departments array, so it's actually a department
                                  }
                                  
                                  return key
                                    .replace(/_/g, ' ')
                                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                                    .replace(/\b\w/g, l => l.toUpperCase());
                                };
                                
                                // Map IDs to readable names based on field context
                                const mapIdToName = (key, value) => {
                                  if (!value) return value;
                                  
                                  const lowerKey = key.toLowerCase();
                                  
                                  // **FALLBACK**: Department field - try correct array first, then fallback to other array for old data
                                  if (lowerKey.includes('department')) {
                                    // Try correct array first (divisionDepartments - 8 parent departments)
                                    let department = referenceData.divisionDepartments?.find(d => {
                                      return d.department_id == value || d.id == value || d.value == value;
                                    });
                                    
                                    window.usedFallbackForDepartment = false;
                                    
                                    // **FALLBACK**: If not found, try the other array (for incorrectly stored old data)
                                    if (!department && referenceData.departments?.length > 0) {
                                      department = referenceData.departments.find(d => {
                                        return d.department_id == value || d.id == value || d.value == value;
                                      });
                                      if (department) {
                                        window.usedFallbackForDepartment = true; // Flag that we used fallback
                                      }
                                    }
                                    
                                    const result = department ? (department.department_name || department.name || department.label) : value;
                                    return result;
                                  }
                                  
                                  // **FALLBACK**: Division field - try correct array first, then fallback to other array for old data
                                  if (lowerKey.includes('division')) {
                                    // Try correct array first (departments - 37 divisions)
                                    let division = referenceData.departments?.find(d => {
                                      return d.department_id == value || d.id == value || d.value == value;
                                    });
                                    
                                    window.usedFallbackForDivision = false;
                                    
                                    // **FALLBACK**: If not found, try the other array (for incorrectly stored old data)
                                    if (!division && referenceData.divisionDepartments?.length > 0) {
                                      division = referenceData.divisionDepartments.find(d => {
                                        return d.department_id == value || d.id == value || d.value == value;
                                      });
                                      if (division) {
                                        window.usedFallbackForDivision = true; // Flag that we used fallback
                                      }
                                    }
                                    
                                    const result = division ? (division.department_name || division.name || division.label) : value;
                                    return result;
                                  }
                                  
                                  // Role/Position mapping (handles "role", "post", "position", etc.)
                                  if ((lowerKey.includes('role') || lowerKey.includes('post') || lowerKey.includes('position')) && referenceData.roles.length > 0) {
                                    const role = referenceData.roles.find(r => 
                                      r.role_id == value || r.id == value || r.value == value
                                    );
                                    const result = role ? (role.role_name || role.name || role.label) : value;
                                    return result;
                                  }
                                  
                                  // User/Staff mapping (handles "user", "staff", "manager", "supervisor", etc.)
                                  if ((lowerKey.includes('user') || lowerKey.includes('staff') || lowerKey.includes('manager') || lowerKey.includes('supervisor')) && referenceData.users.length > 0) {
                                    const user = referenceData.users.find(u => 
                                      u.user_id == value || u.id == value || u.value == value
                                    );
                                    const result = user ? (user.user_full_name || user.name || user.label || user.full_name) : value;
                                    return result;
                                  }
                                  
                                  // System mapping
                                  if ((lowerKey.includes('system') || key === 'system_id') && referenceData.systems.length > 0) {
                                    const system = referenceData.systems.find(s => 
                                      s.asset_id == value || s.id == value || s.value == value
                                    );
                                    return system ? (system.asset_name || system.name || system.label) : value;
                                  }
                                  
                                  // Category mapping
                                  if (lowerKey.includes('category') && referenceData.categories.length > 0) {
                                    const category = referenceData.categories.find(c => 
                                      c.category_id == value || c.id == value || c.value == value
                                    );
                                    return category ? (category.category_name || category.name || category.label) : value;
                                  }
                                  
                                  return value;
                                };
                                
                                // Format the value based on its type
                                const formatValue = (value) => {
                                  if (typeof value === 'boolean') {
                                    return (
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {value ? 'Yes' : 'No'}
                                      </span>
                                    );
                                  }
                                  if (Array.isArray(value)) {
                                    return (
                                      <div className="space-y-1">
                                        {value.map((item, index) => {
                                          const mappedItem = mapIdToName(key, item);
                                          return (
                                            <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1">
                                              {String(mappedItem)}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    );
                                  }
                                  if (typeof value === 'object') {
                                    return (
                                      <pre className="text-xs bg-gray-50 p-2 rounded border overflow-x-auto">
                                        {JSON.stringify(value, null, 2)}
                                      </pre>
                                    );
                                  }
                                  
                                  // Try to map single values
                                  const mappedValue = mapIdToName(key, value);
                                  return String(mappedValue);
                                };
                                
                                return (
                                  <div key={key} className="bg-gray-50 p-3 rounded-lg">
                                    <dt className="text-sm font-medium text-gray-600 mb-1">
                                      {formatKey(key)}
                                    </dt>
                                    <dd className="text-sm text-gray-900">
                                      {formatValue(value)}
                                    </dd>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Attachments */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Paperclip className="w-5 h-5 mr-2 text-blue-600" />
                            Attachments {ticket.attachments ? `(${ticket.attachments.length})` : '(0)'}
                          </h2>
                          
                          {ticket.attachments && ticket.attachments.length > 0 ? (
                            (() => {
                              const images = ticket.attachments.filter(att => 
                                att.mime_type?.startsWith('image/') || att.file_type?.startsWith('image/') ||
                                /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(att.file_name || '')
                              );
                              const files = ticket.attachments.filter(att => 
                                !(att.mime_type?.startsWith('image/') || att.file_type?.startsWith('image/') ||
                                /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(att.file_name || ''))
                              );

                              return (
                                <>
                                  {/* Images Section */}
                                  {images.length > 0 && (
                                    <div className="mb-6">
                                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                        <Eye className="w-4 h-4 mr-2" />
                                        Images ({images.length})
                                      </h3>
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {images.map((image, index) => {
                                          const safeName = (image.file_path || '').toString().split(/[/\\]/).pop();
                                          const fileUrl = `${API_BASE_URL}/download.php?ticket=${ticket.id}&file=${encodeURIComponent(safeName)}`;
                                          return (
                                            <div key={index} className="group relative bg-gray-50 rounded-lg border overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.open(fileUrl + '&v=' + Date.now(), '_blank')}>
                                              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                                <img 
                                                  src={fileUrl} 
                                                  alt={image.file_name || `Image ${index + 1}`}
                                                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                                                  onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center w-full h-full"><Eye class="w-8 h-8 text-gray-400" /></div>';
                                                  }}
                                                />
                                              </div>
                                              <div className="p-2 bg-white border-t">
                                                <p className="text-xs font-medium text-gray-900 truncate" title={image.file_name}>
                                                  {image.file_name || `Image ${index + 1}`}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                  {image.file_size ? `${Math.round(image.file_size / 1024)} KB` : 'Unknown size'}
                                                </p>
                                              </div>
                                              {/* Overlay with actions */}
                                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all pointer-events-none">
                                                <div className="flex space-x-2 pointer-events-auto">
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      window.open(fileUrl, '_blank');
                                                    }}
                                                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                                                    title="View full size"
                                                  >
                                                    <Eye className="w-4 h-4" />
                                                  </button>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const link = document.createElement('a');
                                                      link.href = fileUrl + '&download=1';
                                                      link.download = image.file_name || `image-${index + 1}`;
                                                      link.click();
                                                    }}
                                                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                                                    title="Download"
                                                  >
                                                    <Download className="w-4 h-4" />
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* Files Section */}
                                  {files.length > 0 && (
                                    <div>
                                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                        <FileText className="w-4 h-4 mr-2" />
                                        Files ({files.length})
                                      </h3>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {files.map((file, index) => {
                                          // Determine file icon based on extension
                                          const getFileIcon = (fileName) => {
                                            const ext = fileName?.split('.').pop()?.toLowerCase();
                                            switch (ext) {
                                              case 'pdf': return '📄';
                                              case 'doc': case 'docx': return '📝';
                                              case 'xls': case 'xlsx': return '📊';
                                              case 'ppt': case 'pptx': return '📊';
                                              case 'zip': case 'rar': case '7z': return '🗜️';
                                              case 'txt': return '📄';
                                              default: return '📎';
                                            }
                                          };

                                          const safeDocName = (file.file_path || '').toString().split(/[/\\]/).pop();
                                          const fileUrl = `${API_BASE_URL}/download.php?ticket=${ticket.id}&file=${encodeURIComponent(safeDocName)}`;

                                          return (
                                            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors group">
                                              <div className="text-2xl flex-shrink-0">
                                                {getFileIcon(file.file_name)}
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate" title={file.file_name}>
                                                  {file.file_name || `File ${index + 1}`}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                  {file.file_size ? `${Math.round(file.file_size / 1024)} KB` : 'Unknown size'}
                                                  {(file.file_type || file.mime_type) && ` • ${file.file_type || file.mime_type}`}
                                                </p>
                                              </div>
                                              <div className="flex space-x-2">
                                                <button
                                                  onClick={() => window.open(fileUrl + '&v=' + Date.now(), '_blank')}
                                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                  title="View"
                                                >
                                                  <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    const link = document.createElement('a');
                                                    link.href = fileUrl + '&download=1';
                                                    link.download = file.file_name || `file-${index + 1}`;
                                                    link.click();
                                                  }}
                                                  className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                                                  title="Download"
                                                >
                                                  <Download className="w-4 h-4" />
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <Paperclip className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                              <p>No attachments found for this ticket</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Column - Ticket Properties */}
                      <div className="space-y-6">

                        {/* Approval Action Cards - Show above Ticket Properties */}
                        {isAccessTicket && (() => {
                          // Parse ticket_approvers array for sequential approval
                          const ticketApprovers = ticket?.ticket_approvers ? JSON.parse(ticket.ticket_approvers) : [];
                          if (ticketApprovers.length === 0) return null;
                          
                          // Helper to get approver status
                          const getStatus = (approverType) => {
                            if (approverType === 'manager') return ticket?.ticket_manager || 0;
                            if (approverType === 'it_hod') return ticket?.ticket_it_hod || 0;
                            if (approverType === 'director') return ticket?.ticket_director || 0;
                            if (approverType === 'hod' || approverType.startsWith('hod_')) return ticket?.ticket_hod || 0;
                            return 0;
                          };
                          
                          // Helper to get approver data
                          const getData = (approverType) => {
                            if (approverType === 'manager') return ticket?.manager;
                            if (approverType === 'it_hod') return ticket?.it_hod;
                            if (approverType === 'director') return ticket?.director;
                            if (approverType.startsWith('hod_')) {
                              const userId = approverType.split('_')[1];
                              const hods = ticket?.hods || [];
                              return hods.find(h => h.user_id == userId) || ticket?.hod;
                            }
                            return ticket?.hod;
                          };
                          
                          // Find the next pending approver in sequence
                          let nextApproverIndex = -1;
                          for (let i = 0; i < ticketApprovers.length; i++) {
                            const status = getStatus(ticketApprovers[i]);
                            if (status !== 1) { // Not approved (0=pending, 2=more info, 3=rejected)
                              nextApproverIndex = i;
                              break;
                            }
                          }
                          
                          // If no pending approver found, return null
                          if (nextApproverIndex === -1) return null;
                          
                          const nextApproverType = ticketApprovers[nextApproverIndex];
                          const approverData = getData(nextApproverType);
                          const approverStatus = getStatus(nextApproverType);
                          
                          // Only show if approver data exists and status is pending (0) or more info (2)
                          if (!approverData || (approverStatus !== 0 && approverStatus !== 2)) return null;
                          
                          return (
                            <ApprovalActionCTA
                              approverType={nextApproverType}
                              approverName={approverData.user_full_name}
                              approverEmail={approverData.user_email}
                              approverData={approverData}
                              onApprovalAction={handleApprovalAction}
                              currentUser={currentUser}
                              onResendNotification={handleResendApprovalNotification}
                            />
                          );
                        })()}

                        {/* Declaration Action Card - Show for allocated recipients who need to declare asset receipt */}
                        <DeclarationActionCard
                          ticket={ticket}
                          currentUser={currentUser}
                          onDeclarationSubmit={handleDeclarationSubmit}
                        />

                        {/* Ticket Properties */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                              <Settings className="w-5 h-5 mr-2" />
                              Ticket Properties
                            </h3> 
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Status</label>
                              {isEditing ? (
                                <Select
                                  value={status}
                                  onChange={(e) => setStatus(e.target.value)}
                                  className="w-full text-sm"
                                >
                                  {statuses.map(status => (
                                    <option key={status.value} value={status.value}>
                                      {status.label}
                                    </option>
                                  ))}
                                </Select>
                              ) : (
                                <div className="text-sm font-medium py-1">
                                  <span 
                                    className="px-2 py-1 rounded-full text-white text-xs"
                                    style={{ backgroundColor: getStatusColor(status) }}
                                  >
                                    {status?.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Priority</label>
                              {isEditing ? (
                                <Select
                                  value={priority}
                                  onChange={(e) => setPriority(e.target.value)}
                                  className="w-full text-sm"
                                >
                                  {priorities.map(priority => (
                                    <option key={priority.value} value={priority.value}>
                                      {priority.label}
                                    </option>
                                  ))}
                                </Select>
                              ) : (
                                <div className="text-sm font-medium py-1" style={{ color: getPriorityColor(priority) }}>
                                  {priority?.toUpperCase()}
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Ticket Type</label>
                              {isEditing ? (
                                <Select
                                  value={ticketType}
                                  onChange={(e) => setTicketType(e.target.value)}
                                  className="w-full text-sm"
                                >
                                  {ticketTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                      {type.label}
                                    </option>
                                  ))}
                                </Select>
                              ) : (
                                <div className="text-sm font-semibold text-gray-900 py-1">
                                  {ticketTypes.find(type => type.value === ticketType)?.label || ticketType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Assigned To</label>
                              {isEditing ? (
                                <Select
                                  value={assignedTo}
                                  onChange={(e) => setAssignedTo(e.target.value)}
                                  className="w-full text-sm"
                                >
                                  <option value="">Unassigned</option>
                                  {itUsers.map(user => (
                                    <option key={user.user_id} value={user.user_id}>
                                      {user.user_full_name}
                                    </option>
                                  ))}
                                </Select>
                              ) : (
                                <div className="text-sm font-semibold text-gray-900 py-1">
                                  {(() => {
                                    if (!assignedTo) return 'Unassigned';
                                    const assignedUser = itUsers.find(user => user.user_id === assignedTo);
                                    return assignedUser ? assignedUser.user_full_name : (ticket.assigned_to_name || 'Unknown User');
                                  })()}
                                </div>
                              )}
                            </div>

                            {/* Created - only show in view mode */}
                            {!isEditing && (
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Created</label>
                                <div className="text-sm font-semibold text-gray-900 py-1">{formatDate(ticket.created_at)}</div>
                              </div>
                            )}

                            {/* Save Changes */}
                            {isEditing && (
                              <div className="pt-3 border-t border-gray-200">
                                <Button
                                  onClick={handleSaveChanges}
                                  disabled={saving}
                                  icon={Save}
                                  className="w-full text-sm py-2"
                                >
                                  {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Related Tickets - Only visible to IT users */}
                        {isIT() && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                              <Link2 className="w-5 h-5 mr-2" />
                              Related Tickets
                            </h3>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-end gap-3 flex-wrap">
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Related Ticket Number</label>
                                <input type="text" value={linkForm.relatedNumber} onChange={(e)=>setLinkForm(f=>({...f, relatedNumber: e.target.value}))} className="w-56 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g. TK000007" />
                              </div>
                              <div className="relative" ref={relationHelpRef}>
                                <div className="flex items-center justify-between">
                                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Relation</label>
                                  <button type="button" onClick={() => setShowRelationHelp(v=>!v)} className="ml-2 text-gray-400 hover:text-gray-600" aria-label="Relation types help" title="What do these relation types mean?">
                                    <HelpCircle className="w-4 h-4" />
                                  </button>
                                </div>
                                {showRelationHelp && (
                                  <div
                                    ref={relationHelpPopoverRef}
                                    className={`absolute z-50 w-96 right-0 ${relationHelpPlacement === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'} p-4 bg-white border border-gray-200 rounded-lg shadow-2xl text-gray-800`}
                                    role="tooltip"
                                  >
                                    {/* Arrow */}
                                    <span className={`pointer-events-none absolute w-3 h-3 bg-white border border-gray-200 rotate-45 ${relationHelpPlacement === 'bottom' ? '-top-1 right-6 border-b-0 border-r-0' : '-bottom-1 right-6 border-t-0 border-l-0'}`}></span>
                                    <div className="text-[14px] font-semibold mb-3">Relation types</div>
                                    <dl className="space-y-2 text-[13.5px] leading-6">
                                      <div className="flex gap-3">
                                        <dt className="shrink-0"><span className="inline-block px-2 py-0.5 rounded-md bg-gray-100 text-gray-800 border border-gray-200 text-xs font-medium">Related</span></dt>
                                        <dd className="text-gray-700">Connected context; no automatic changes.</dd>
                                      </div>
                                      <div className="flex gap-3">
                                        <dt className="shrink-0"><span className="inline-block px-2 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-200 text-xs font-medium">Duplicate</span></dt>
                                        <dd className="text-gray-700">Same issue; keep one primary and close others as duplicates.</dd>
                                      </div>
                                      <div className="flex gap-3">
                                        <dt className="shrink-0"><span className="inline-block px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">Caused By</span></dt>
                                        <dd className="text-gray-700">This ticket exists due to the other ticket <span className="italic">(directional)</span>.</dd>
                                      </div>
                                      <div className="flex gap-3">
                                        <dt className="shrink-0"><span className="inline-block px-2 py-0.5 rounded-md bg-blue-50 text-blue-800 border border-blue-200 text-xs font-medium">Parent</span></dt>
                                        <dd className="text-gray-700">Current ticket is the parent work item <span className="italic">(directional)</span>.</dd>
                                      </div>
                                      <div className="flex gap-3">
                                        <dt className="shrink-0"><span className="inline-block px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-800 border border-indigo-200 text-xs font-medium">Child</span></dt>
                                        <dd className="text-gray-700">Current ticket is a sub-task of the other <span className="italic">(directional)</span>.</dd>
                                      </div>
                                    </dl>
                                  </div>
                                )}
                                <select value={linkForm.relationType} onChange={(e)=>setLinkForm(f=>({...f, relationType: e.target.value}))} className="w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                  <option value="related">Related</option>
                                  <option value="duplicate">Duplicate</option>
                                  <option value="caused_by">Caused By</option>
                                  <option value="parent">Parent</option>
                                  <option value="child">Child</option>
                                </select>
                              </div>
                              <Button onClick={handleCreateLink} disabled={linking || !linkForm.relatedNumber} className="h-10">
                                {linking ? 'Linking...' : 'Link Ticket'}
                              </Button>
                            </div>

                            <div className="border-t border-gray-200 pt-3">
                              {related && related.length > 0 ? (
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {related.map((r) => (
                                    <div key={r.link_id} className="flex items-start justify-between p-2 rounded border bg-gray-50">
                                      <div className="min-w-0">
                                        <div className="text-sm font-medium text-gray-900">{r.ticket?.ticket_number || `#${r.ticket?.ticket_id}`}</div>
                                        <div className="text-xs text-gray-600 truncate w-64">{r.ticket?.title || r.ticket?.subject || 'Untitled'}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">{r.relation_type}</div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button 
                                          variant="secondary" 
                                          onClick={() => handleDeleteLink(r.link_id)} 
                                          disabled={unlinking === r.link_id}
                                          className="text-xs"
                                        >
                                          {unlinking === r.link_id ? 'Unlinking...' : 'Unlink'}
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">No related tickets</div>
                              )}
                            </div>
                          </div>
                        </div>
                        )}

                        {/* Work Timing Section */}
                        {(ticket?.status === 'in_progress' || ticket?.status === 'paused' || ticket?.total_work_time > 0) && (
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Clock className="w-5 h-5 mr-2" />
                                Work Timing
                              </h3>
                            </div>
                            <div className="space-y-3">
                              {ticket?.total_work_time > 0 && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Total Work Time</label>
                                  <div className="text-sm font-semibold text-gray-900 py-1">
                                    {ticket.timing?.formatted_total_time || `${Math.floor(ticket.total_work_time / 3600)}h ${Math.floor((ticket.total_work_time % 3600) / 60)}m`}
                                  </div>
                                </div>
                              )}
                              
                              {ticket?.status === 'in_progress' && ticket?.work_started_at && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Current Session</label>
                                  <div className="text-sm font-semibold text-blue-600 py-1">
                                    <span className="inline-flex items-center">
                                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                                      Working since {new Date(ticket.work_started_at).toLocaleTimeString()}
                                    </span>
                                  </div>
                                </div>
                              )}
                              
                              {ticket?.status === 'paused' && ticket?.last_paused_at && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Last Paused</label>
                                  <div className="text-sm font-semibold text-orange-600 py-1">
                                    <span className="inline-flex items-center">
                                      <Pause className="w-3 h-3 mr-2" />
                                      {formatDate(ticket.last_paused_at)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Approval History (for approval tickets) */}
                        {ticket?.requires_approval && ticket?.approval_history && (
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                              <Shield className="w-5 h-5 mr-2" />
                              Approval History
                            </h3>
                            <div className="space-y-3 max-h-48 overflow-y-auto">
                              {ticket.approval_history.map((approval, index) => (
                                <div key={index} className="text-sm border-l-2 border-gray-200 pl-3">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-gray-900">{approval.approver}</span>
                                    <span className={`px-2 py-1 rounded text-sm ${
                                      approval.action === 'approved' ? 'bg-green-100 text-green-800' :
                                      approval.action === 'rejected' ? 'bg-red-100 text-red-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {approval.action}
                                    </span>
                                  </div>
                                  <p className="text-gray-500 mt-1">{approval.timestamp}</p>
                                  {approval.notes && (
                                    <p className="text-gray-600 mt-1 italic">"{approval.notes}"</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'workflow' && (
                  <div className="max-w-4xl mx-auto space-y-6">
                    {/* Cross-Module Integration Status Panel - Only visible to IT users */}
                    {isIT && (
                      <IntegrationStatusPanel 
                        ticket={ticket} 
                        onRefresh={fetchTicketDetails} 
                      />
                    )}

                    {/* Implementation Execution Panel - Show only after all approvals complete */}
                    {currentWorkflowStage?.stage === 'implementation' && (
                      <ImplementationPanel 
                        ticket={ticket} 
                        onIntegrationExecuted={(integrationType, result) => {
                          // Refresh ticket details to show updated integration status
                          fetchTicketDetails();
                          toast.success(`${integrationType.replace('_', ' ')} integration completed successfully`);
                        }} 
                      />
                    )}

                    {isAccessTicket ? (
                      <>
                        {/* Workflow Status Summary - Always visible */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Progress</h3>
                          
                          {/* Assessment Status - Only for change_request and enhancement tickets */}
                          {(ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement') && (
                            <div className="flex items-center mb-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${ticket?.ticket_assessment === 1 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                {ticket?.ticket_assessment === 1 ? <Check className="w-5 h-5" /> : '1'}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">Assessment</div>
                                <div className="text-sm text-gray-600">
                                  {ticket?.ticket_assessment === 1 ? 'Completed' : 'Pending IT assessment'}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Approver Selection Status */}
                          <div className="flex items-center mb-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${ticket?.ticket_recommended === 1 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                              {ticket?.ticket_recommended === 1 ? <Check className="w-5 h-5" /> : ((ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement') ? '2' : '1')}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">Approver Selection</div>
                              <div className="text-sm text-gray-600">
                                {ticket?.ticket_recommended === 1 ? `Approvers: ${approvers.map(approverType => {
                                  // Format approver names
                                  if (approverType === 'manager') return ticket?.manager?.user_full_name || 'Manager';
                                  if (approverType === 'it_hod') return ticket?.it_hod?.user_full_name || 'IT HOD';
                                  if (approverType === 'director') return ticket?.director?.user_full_name || 'Director';
                                  // Handle specific HOD (hod_65)
                                  if (approverType.startsWith('hod_')) {
                                    const userId = approverType.split('_')[1];
                                    const hods = ticket?.hods || [];
                                    const hod = hods.find(h => h.user_id == userId);
                                    return hod?.user_full_name || `HOD (${hod?.dept_name || 'Department'})`;
                                  }
                                  return ticket?.hod?.user_full_name || 'HOD';
                                }).join(', ')}` : 'Awaiting approver selection'}
                              </div>
                            </div>
                          </div>

                          {/* Individual Approval Steps - Show each approver with status and completion date */}
                          {approvers.length > 0 && (
                            <div className="ml-4 border-l-2 border-gray-200 pl-4 mb-3 space-y-3">
                              {approvers.map((approverType, index) => {
                                // Determine approval status for this approver
                                let status = 0;
                                let approverName = '';
                                let levelLabel = '';
                                
                                if (approverType === 'manager') {
                                  status = ticket?.ticket_manager || 0;
                                  approverName = ticket?.manager?.user_full_name || 'Manager';
                                  levelLabel = 'Manager/Supervisor';
                                } else if (approverType === 'it_hod') {
                                  status = ticket?.ticket_it_hod || 0;
                                  approverName = ticket?.it_hod?.user_full_name || 'IT HOD';
                                  levelLabel = 'IT HOD';
                                } else if (approverType === 'director') {
                                  status = ticket?.ticket_director || 0;
                                  approverName = ticket?.director?.user_full_name || 'Director';
                                  levelLabel = 'Director';
                                } else if (approverType.startsWith('hod_')) {
                                  status = ticket?.ticket_hod || 0;
                                  const userId = approverType.split('_')[1];
                                  const hods = ticket?.hods || [];
                                  const hod = hods.find(h => h.user_id == userId);
                                  approverName = hod?.user_full_name || 'HOD';
                                  levelLabel = hod?.dept_name ? `HOD (${hod.dept_name})` : 'Department HOD';
                                } else if (approverType === 'hod') {
                                  status = ticket?.ticket_hod || 0;
                                  approverName = ticket?.hod?.user_full_name || 'HOD';
                                  levelLabel = 'Department HOD';
                                }

                                // Find the approval record with completion date from approval_status
                                let approvalRecord = (ticket?.approval_status || []).find(a => {
                                  // Match by level or role
                                  if (approverType === 'manager' && (a.approval_level == 1 || a.required_role === 'manager_supervisor')) return true;
                                  if ((approverType === 'hod' || approverType.startsWith('hod_')) && (a.approval_level == 2 || a.required_role === 'hod')) return true;
                                  if (approverType === 'it_hod' && (a.approval_level == 3 || a.required_role === 'it_hod_director')) return true;
                                  if (approverType === 'director' && (a.approval_level == 4 || a.required_role === 'director')) return true;
                                  return false;
                                });

                                // If no approval_status record, try to find approval date from comments
                                let completedAt = approvalRecord?.decided_at;
                                
                                if (!completedAt && status === 1) {
                                  // Look for approval comment in messages/comments
                                  const allComments = ticket?.messages || ticket?.comments || ticket?.conversation || [];
                                  const approvalComment = allComments.find(c => {
                                    const content = (c.content || c.message || '').toLowerCase();
                                    const commentType = c.comment_type || c.type || '';
                                    
                                    // Check if this is an approval comment for this level
                                    if (commentType === 'approval' || content.includes('approval decision')) {
                                      if (approverType === 'manager' && content.includes('manager')) return true;
                                      if ((approverType === 'hod' || approverType.startsWith('hod_')) && (content.includes('hod') || content.includes('head of department'))) return true;
                                      if (approverType === 'it_hod' && content.includes('it_hod')) return true;
                                      if (approverType === 'director' && content.includes('director')) return true;
                                    }
                                    return false;
                                  });
                                  
                                  if (approvalComment) {
                                    completedAt = approvalComment.created_at || approvalComment.timestamp;
                                  }
                                }

                                return (
                                  <div key={index} className="flex items-start">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                                      status === 1 ? 'bg-green-100 text-green-600' : 
                                      status === 3 ? 'bg-red-100 text-red-600' : 
                                      status === 2 ? 'bg-yellow-100 text-yellow-600' :
                                      'bg-gray-100 text-gray-400'
                                    }`}>
                                      {status === 1 ? <Check className="w-4 h-4" /> : 
                                       status === 3 ? <XCircle className="w-4 h-4" /> : 
                                       status === 2 ? <Clock className="w-4 h-4" /> :
                                       <Clock className="w-4 h-4" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium text-gray-800 text-sm">{levelLabel}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          status === 1 ? 'bg-green-100 text-green-700' : 
                                          status === 3 ? 'bg-red-100 text-red-700' : 
                                          status === 2 ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                          {status === 1 ? 'Approved' : 
                                           status === 3 ? 'Rejected' : 
                                           status === 2 ? 'More Info Requested' :
                                           'Pending'}
                                        </span>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        {approverName}
                                        {completedAt && (
                                          <span className="ml-2 text-gray-400">
                                            • {formatDate(completedAt)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Overall Approval Status Summary */}
                          {approvers.length > 0 && (
                            <div className="flex items-center mb-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                                (ticket?.status === 'rejected' || 
                                 (approvers.includes('manager') && ticket?.ticket_manager === 3) ||
                                 (approvers.includes('hod') && ticket?.ticket_hod === 3) ||
                                 (approvers.includes('it_hod') && ticket?.ticket_it_hod === 3) ||
                                 (approvers.includes('director') && ticket?.ticket_director === 3))
                                ? 'bg-red-100 text-red-600' 
                                : ticket?.ticket_status === 3 || 
                                  (approvers.includes('director') && ticket?.ticket_director === 1) ||
                                  (approvers.includes('it_hod') && !approvers.includes('director') && ticket?.ticket_it_hod === 1) ||
                                  (approvers.includes('hod') && !approvers.includes('it_hod') && !approvers.includes('director') && ticket?.ticket_hod === 1) ||
                                  (approvers.includes('manager') && approvers.length === 1 && ticket?.ticket_manager === 1)
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-blue-100 text-blue-600'
                              }`}>
                                {(ticket?.status === 'rejected' || 
                                 (approvers.includes('manager') && ticket?.ticket_manager === 3) ||
                                 (approvers.includes('hod') && ticket?.ticket_hod === 3) ||
                                 (approvers.includes('it_hod') && ticket?.ticket_it_hod === 3) ||
                                 (approvers.includes('director') && ticket?.ticket_director === 3))
                                ? <XCircle className="w-5 h-5" />
                                : (ticket?.ticket_status === 3 || 
                                  (approvers.includes('director') && ticket?.ticket_director === 1) ||
                                  (approvers.includes('it_hod') && !approvers.includes('director') && ticket?.ticket_it_hod === 1) ||
                                  (approvers.includes('hod') && !approvers.includes('it_hod') && !approvers.includes('director') && ticket?.ticket_hod === 1) ||
                                  (approvers.includes('manager') && approvers.length === 1 && ticket?.ticket_manager === 1))
                                ? <Check className="w-5 h-5" /> 
                                : ((ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement') ? '3' : '2')
                              }
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">Approval Process</div>
                                <div className="text-sm text-gray-600">
                                  {ticket?.status === 'rejected' ? 'Request rejected' : currentWorkflowStage?.message}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Implementation Status */}
                          {ticket?.ticket_status === 3 && (
                            <div className="flex items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${ticket?.ticket_outcome > 0 ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                                {ticket?.ticket_outcome > 0 ? <Check className="w-5 h-5" /> : ((ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement') ? '4' : '3')}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">Implementation</div>
                                <div className="text-sm text-gray-600">
                                  {ticket?.ticket_outcome > 0 ? (
                                    <>
                                      Implementation completed
                                      {ticket?.work_completed_at && (
                                        <span className="ml-2 text-gray-400">• {formatDate(ticket.work_completed_at)}</span>
                                      )}
                                    </>
                                  ) : 'Ready for IT implementation'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* IT Assessment Details - Show when assessment is completed */}
                        {ticket?.ticket_assessment === 1 && (ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement') && (
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                              <FileText className="w-5 h-5 mr-2 text-blue-600" />
                              IT Assessment Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Business Activities Affected */}
                              <div className="bg-gray-50 rounded p-2">
                                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Business Activities Affected</h4>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">{ticket?.ticket_affected || 'Not specified'}</p>
                              </div>
                              
                              {/* Possible Impact */}
                              <div className="bg-gray-50 rounded p-2">
                                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Possible Impact</h4>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">{ticket?.ticket_impacted || 'Not specified'}</p>
                              </div>
                              
                              {/* Mitigation Control */}
                              <div className="bg-gray-50 rounded p-2">
                                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Mitigation Control</h4>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">{ticket?.ticket_mitigation || 'Not specified'}</p>
                              </div>
                              
                              {/* IT Recommendation */}
                              <div className={`rounded p-2 ${ticket?.ticket_recommended === 1 ? 'bg-green-50 border border-green-200' : ticket?.ticket_recommended === 0 ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">IT Recommendation</h4>
                                <div className="flex items-center">
                                  {ticket?.ticket_recommended === 1 ? (
                                    <>
                                      <Check className="w-4 h-4 text-green-600 mr-1" />
                                      <span className="text-sm text-green-700 font-medium">Recommended</span>
                                    </>
                                  ) : ticket?.ticket_recommended === 0 ? (
                                    <>
                                      <XCircle className="w-4 h-4 text-red-600 mr-1" />
                                      <span className="text-sm text-red-700 font-medium">Not Recommended</span>
                                    </>
                                  ) : (
                                    <span className="text-sm text-gray-500">Pending</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Detailed Approver Status - Show when approvers are selected */}
                        {ticket?.ticket_recommended === 1 && approvers.length > 0 && (
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-gray-900">Approver Status</h3>
                              {isIT() && (
                                <button
                                  onClick={() => handleResetApprovers()}
                                  className="px-3 py-1.5 text-sm bg-orange-100 text-orange-700 border border-orange-300 rounded-md hover:bg-orange-200 flex items-center"
                                  title="Reset approval workflow and reconfigure approvers"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Reset Workflow
                                </button>
                              )}
                            </div>
                            <div className="space-y-4">
                              {(() => {
                                // Parse ticket_approvers array for dynamic display
                                const ticketApprovers = ticket?.ticket_approvers ? JSON.parse(ticket.ticket_approvers) : [];
                                
                                // Helper function to get approver status
                                const getApproverStatus = (approverType) => {
                                  if (approverType === 'manager') return ticket?.ticket_manager || 0;
                                  if (approverType === 'it_hod') return ticket?.ticket_it_hod || 0;
                                  if (approverType === 'director') return ticket?.ticket_director || 0;
                                  // Handle specific HOD (hod_65) or generic hod - both use ticket_hod column
                                  if (approverType === 'hod' || approverType.startsWith('hod_')) return ticket?.ticket_hod || 0;
                                  return 0;
                                };
                                
                                // Helper function to get approver data
                                const getApproverData = (approverType) => {
                                  if (approverType === 'manager') return ticket?.manager;
                                  if (approverType === 'it_hod') return ticket?.it_hod;
                                  if (approverType === 'director') return ticket?.director;
                                  // For specific HOD (hod_65), extract user_id and find in hods array
                                  if (approverType.startsWith('hod_')) {
                                    const userId = approverType.split('_')[1];
                                    const hods = ticket?.hods || [];
                                    return hods.find(h => h.user_id == userId) || ticket?.hod;
                                  }
                                  return ticket?.hod;
                                };
                                
                                // Helper function to get approver label
                                const getApproverLabel = (approverType, approverData) => {
                                  if (approverType === 'manager') return 'Manager';
                                  if (approverType === 'it_hod') return 'IT Head of Department';
                                  if (approverType === 'director') return 'Director';
                                  if (approverType.startsWith('hod_')) {
                                    // Show department name if available
                                    return approverData?.dept_name ? `HOD - ${approverData.dept_name}` : 'Head of Department';
                                  }
                                  return 'Head of Department';
                                };
                                
                                // Helper function to get approval date from approval_status
                                const getApprovalDate = (approverType) => {
                                  const approvalLevelMap = { 'manager': 1, 'hod': 2, 'it_hod': 3, 'director': 4 };
                                  const normalizedType = approverType.startsWith('hod_') ? 'hod' : approverType;
                                  const levelNum = approvalLevelMap[normalizedType];
                                  
                                  // Look in approval_status array
                                  const approvalRecord = (ticket?.approval_status || []).find(a => 
                                    a.approval_level == levelNum || a.required_role === normalizedType
                                  );
                                  if (approvalRecord?.decided_at) return approvalRecord.decided_at;
                                  
                                  // Fallback: Look in comments for approval action
                                  const allComments = ticket?.messages || ticket?.comments || ticket?.conversation || [];
                                  const approvalComment = allComments.find(c => {
                                    const content = (c.content || c.message || '').toLowerCase();
                                    const commentType = c.comment_type || c.type || '';
                                    if (commentType === 'approval_action' || commentType === 'approval' || content.includes('approved the request')) {
                                      if (approverType === 'manager' && content.includes('manager')) return true;
                                      if ((approverType === 'hod' || approverType.startsWith('hod_')) && (content.includes('hod') || content.includes('head of department'))) return true;
                                      if (approverType === 'it_hod' && content.includes('it')) return true;
                                      if (approverType === 'director' && content.includes('director')) return true;
                                    }
                                    return false;
                                  });
                                  return approvalComment?.created_at || approvalComment?.timestamp || null;
                                };
                                
                                // Helper function to determine "Awaiting X Approval" status
                                const getAwaitingStatus = (currentIndex) => {
                                  // Find first unapproved approver before current one
                                  for (let i = currentIndex - 1; i >= 0; i--) {
                                    const prevApproverType = ticketApprovers[i];
                                    const prevStatus = getApproverStatus(prevApproverType);
                                    if (prevStatus !== 1) { // Not approved
                                      const prevData = getApproverData(prevApproverType);
                                      const prevLabel = getApproverLabel(prevApproverType, prevData);
                                      return `Awaiting ${prevLabel} Approval`;
                                    }
                                  }
                                  return 'Pending';
                                };
                                
                                // Render each approver dynamically
                                return ticketApprovers.map((approverType, index) => {
                                  const approverData = getApproverData(approverType);
                                  const status = getApproverStatus(approverType);
                                  const label = getApproverLabel(approverType, approverData);
                                  const approvalDate = status === 1 ? getApprovalDate(approverType) : null;
                                  
                                  if (!approverData) return null;
                                  
                                  return (
                                    <div key={`${approverType}-${index}`} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                                      <div className="flex items-center flex-1">
                                        <User className="w-5 h-5 text-gray-400 mr-3" />
                                        <div>
                                          <div className="font-medium text-gray-900">{approverData.user_full_name}</div>
                                          <div className="text-sm text-gray-600">{label}</div>
                                          {approvalDate && (
                                            <div className="text-xs text-gray-400 mt-0.5">{formatDate(approvalDate)}</div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center">
                                        {status === 1 ? (
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            <Check className="w-3 h-3 mr-1" /> Approved
                                          </span>
                                        ) : status === 2 ? (
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            <AlertCircle className="w-3 h-3 mr-1" /> More Info Needed
                                          </span>
                                        ) : status === 3 ? (
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            <XCircle className="w-3 h-3 mr-1" /> Rejected
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            <Clock className="w-3 h-3 mr-1" /> {getAwaitingStatus(index)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Assessment Step - Only for change_request and enhancement tickets */}
                        {currentWorkflowStage?.stage === 'assessment' && (ticket?.ticket_type === 'change_request' || ticket?.ticket_type === 'enhancement') && isIT() && (
                          <AssessmentPanel
                            onAssessmentSubmission={handleAssessmentSubmission}
                      ticket={ticket} 
                          />
                        )}

                        {/* Approver Selection - After assessment is complete */}
                        {currentWorkflowStage?.stage === 'approver_selection' && isIT() && (
                          <ApproverSelection
                            availableApprovers={{
                              manager: ticket?.manager,
                              hod: ticket?.hod,
                              it_hod: ticket?.it_hod,
                              director: ticket?.director
                            }}
                            allHods={(ticket?.hods || []).filter(hod => hod.user_id !== ticket?.it_hod?.user_id)}
                            onApproverSelection={handleApproverSelection}
                            currentApprovers={approvers}
                            ticket={ticket}
                            onResetApprovers={handleResetApprovers}
                          />
                        )}

                        {/* Approval Action - Show only for the NEXT approver in sequence */}
                        {(() => {
                          // Parse ticket_approvers array for sequential approval
                          const ticketApprovers = ticket?.ticket_approvers ? JSON.parse(ticket.ticket_approvers) : [];
                          if (ticketApprovers.length === 0) return null;
                          
                          // Helper to get approver status
                          const getStatus = (approverType) => {
                            if (approverType === 'manager') return ticket?.ticket_manager || 0;
                            if (approverType === 'it_hod') return ticket?.ticket_it_hod || 0;
                            if (approverType === 'director') return ticket?.ticket_director || 0;
                            if (approverType === 'hod' || approverType.startsWith('hod_')) return ticket?.ticket_hod || 0;
                            return 0;
                          };
                          
                          // Helper to get approver data
                          const getData = (approverType) => {
                            if (approverType === 'manager') return ticket?.manager;
                            if (approverType === 'it_hod') return ticket?.it_hod;
                            if (approverType === 'director') return ticket?.director;
                            if (approverType.startsWith('hod_')) {
                              const userId = approverType.split('_')[1];
                              const hods = ticket?.hods || [];
                              return hods.find(h => h.user_id == userId) || ticket?.hod;
                            }
                            return ticket?.hod;
                          };
                          
                          // Find the next pending approver in sequence
                          let nextApproverIndex = -1;
                          for (let i = 0; i < ticketApprovers.length; i++) {
                            const status = getStatus(ticketApprovers[i]);
                            if (status !== 1) { // Not approved (0=pending, 2=more info, 3=rejected)
                              nextApproverIndex = i;
                              break;
                            }
                          }
                          
                          // If no pending approver found, return null
                          if (nextApproverIndex === -1) return null;
                          
                          const nextApproverType = ticketApprovers[nextApproverIndex];
                          const approverData = getData(nextApproverType);
                          const approverStatus = getStatus(nextApproverType);
                          
                          // Only show if approver data exists and status is pending (0) or more info (2)
                          if (!approverData || (approverStatus !== 0 && approverStatus !== 2)) return null;
                          
                          return (
                            <ApprovalActionCTA
                              approverType={nextApproverType}
                              approverName={approverData.user_full_name}
                              approverEmail={approverData.user_email}
                              approverData={approverData}
                              onApprovalAction={handleApprovalAction}
                              currentUser={currentUser}
                              onResendNotification={handleResendApprovalNotification}
                            />
                          );
                        })()}

                        {/* Implementation Outcome - For IT staff when ready */}
                        {currentWorkflowStage?.stage === 'implementation' && isIT() && (
                          <ImplementationOutcome
                            onImplementationOutcome={handleImplementationOutcome}
                            ticketType={ticket.ticket_type}
                          />
                        )}

                        {/* Show completion status */}
                        {currentWorkflowStage?.stage === 'completed' && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Check className="w-6 h-6 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-green-900 mb-2">
                              Implementation Complete
                            </h3>
                            <p className="text-green-700">
                              This access request has been successfully implemented.
                            </p>
                          </div>
                        )}

                        {/* Integration components moved to top level for visibility */}
                      </>
                    ) : (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="text-center py-8">
                          <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No Workflow Required
                          </h3>
                          <p className="text-gray-600 mb-6">
                            This ticket doesn't require workflow processing. It's a standard IT support request.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'delegation' && (
                  <div className="max-w-4xl mx-auto">
                    <DelegationPanel 
                      userRole={userRole} 
                      onSetDelegate={handleSetDelegate} 
                    />
                  </div>
                )}

                {activeTab === 'conversation' && (
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                          <History className="w-5 h-5 mr-2 text-blue-600" />
                          Conversation History
                        </h2>
                      </div>
                      <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
                        {ticket.messages && ticket.messages.length > 0 ? (
                          ticket.messages.map((message, index) => (
                            <div key={index} className="flex space-x-4">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-blue-600" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  {(message.user_name || message.author) && (
                                    <span className="font-medium text-gray-900">
                                      {message.user_name || message.author}
                                    </span>
                                  )}
                                  <span className="text-sm text-gray-500">
                                    {formatDate(message.created_at || message.timestamp)}
                                  </span>
                                  {message.type === 'internal_note' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                      Internal
                                    </span>
                                  )}
                                </div>
                                <div className="prose prose-sm max-w-none">
                                  <div className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                                    {(() => {
                                      const content = message.message || message.content || 'No content';
                                      if (typeof content === 'string' && content.includes('<')) {
                                        return (
                                          <div dangerouslySetInnerHTML={{
                                            __html: content
                                              .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                                              .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                                              .replace(/<link[^>]*>/gi, '')
                                              .replace(/<form[^>]*>[\s\S]*?<\/form>/gi, '')
                                              .replace(/<img([^>]*)>/gi, '<img$1 class="img-fluid rounded max-w-full h-auto" />')
                                              .replace(/\n/g, '<br>')
                                          }} />
                                        );
                                      }
                                      return <div className="whitespace-pre-line">{content}</div>;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-12">
                            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500">No conversation history yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'reply' && (
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <Reply className="w-5 h-5 mr-2 text-blue-600" />
                        Send Public Reply
                      </h2>
                      
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reply Content
                          </label>
                          <WysiwygEditor
                            value={replyContent}
                            onChange={setReplyContent}
                            placeholder="Type your reply to the customer here..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Attachments
                          </label>
                          <MultiFileInput
                            files={selectedFiles}
                            onChange={setSelectedFiles}
                            multiple={true}
                          />
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setReplyContent('');
                              setSelectedFiles([]);
                            }}
                          >
                            Clear
                          </Button>
                          <Button
                            onClick={handleSendReply}
                            disabled={!replyContent.trim() || saving}
                            icon={Send}
                          >
                            {saving ? 'Sending...' : 'Send Reply'}
                          </Button>
                          <Button
                            onClick={handleSendAndClose}
                            disabled={!replyContent.trim() || saving}
                            icon={Check}
                          >
                            {saving ? 'Sending...' : 'Send & Close'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'internal' && (
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <Edit3 className="w-5 h-5 mr-2 text-blue-600" />
                        Add Internal Note
                      </h2>
                      
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Internal Note Content
                          </label>
                          <WysiwygEditor
                            value={internalNote}
                            onChange={setInternalNote}
                            placeholder="Add an internal note for your team (not visible to customer)..."
                          />
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                          <Button
                            variant="secondary"
                            onClick={() => setInternalNote('')}
                          >
                            Clear
                          </Button>
                          <Button
                            onClick={handleAddInternalNote}
                            disabled={!internalNote.trim() || saving}
                            icon={Save}
                          >
                            {saving ? 'Saving...' : 'Add Internal Note'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'escalation' && (
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="p-6">
                        <EscalationTab 
                          ticket={ticket}
                          onTicketUpdate={() => {
                            fetchTicketDetails();
                            if (onTicketUpdate) {
                              onTicketUpdate();
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'supplier_escalation' && (
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="p-6">
                        <SupplierEscalationTab 
                          ticket={ticket}
                          selectedSupplier={selectedSupplier}
                          onTicketUpdate={() => {
                            fetchTicketDetails();
                            if (onTicketUpdate) {
                              onTicketUpdate();
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'billing' && (
                  <div className="max-w-4xl mx-auto">
                    <BillingManagementTab 
                      ticket={ticket}
                      onTicketUpdate={() => {
                        fetchTicketDetails();
                        if (onTicketUpdate) {
                          onTicketUpdate();
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Failed to load ticket details</p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default TicketDetailsModal;
